// Полный n8n-workflow — КАНОН черновика сценария (этап A2 — docs/archive/BUILDER-QUALITY-SYNC-PLAN.md).
// Строитель собирает workflow БЕЗ ограничений формата: любые узлы, связи, ветки,
// параллельные и error-выходы — как при работе с n8n напрямую. Лента (graph.ts) осталась
// ТОЛЬКО отрисовкой (n8nToGraph); в путь записи/деплоя она больше не входит.
// Файл ЧИСТЫЙ (без React/DOM/node): его импортируют web и MCP-edge.
//
// INVARIANT(scriptera): канон черновика = scenario_drafts.workflow_json (полный n8n-JSON);
// колонка graph — производная для отрисовки ленты. Причина: лента не выражает fan-out —
// сборка через неё превращала параллельные ветки в цепочку (разбор «3 аниме-варианта»,
// 2026-07-05). Канон: docs/archive/BUILDER-QUALITY-SYNC-PLAN.md (заказ владельца, п.1).

import {
  allowedParamsHint,
  canonicalNodeType,
  isEmptyRequiredParamValue,
  isEmptyValue,
  isWebhookNodeType,
  localizeDisplayConditions,
  nodePortsFor,
  paramVisible,
  requiredParamMessage,
  resolveParamDefaults,
  stepCredTypes,
  webhookIdFor,
  type CatalogNodeInfo,
  type CatalogParam,
  type InstanceCredRef,
  type LintIssue,
  type LintResult,
  type MissingCred,
} from './graph';
import {
  screenFileButtons,
  screenFromNodeParams,
  screenNextButtons,
} from 'n8n-nodes-scriptera-ai/screen-format';
import { channelNotes } from './channels';
// Ссылка на файл, до которой дотянется площадка — общий чистый хелпер вложений
// (импорт типов оттуда обратно в workflow.ts стирается компилятором, цикла нет).
import { isOurFileUrl, reachableFileUrl } from './chat-attach';
import { condRowPhoneWait, readChatSwitch } from './chat-cond';
import { withContactLoaders } from './contact-load';
import { withCondPairs } from './cond-pair';
import { phoneAskPresent, withPhoneAsk } from './chat-phone';
import { GUARD_NODE_TYPE, withStepGuards } from './step-guard';
import { withIgProfileCred, withTgMessageCred } from './ig-profile-cred';
import { injectPayReceipts } from './pay-receipt';
import { injectPayReturnDefaults } from './pay-return';
import { botAccountState } from './bot-account';
import { lintDataFlow } from './data-flow';
import { workflowDiagram, workflowPorts, type DiagramEdge } from './diagram';

// ── Типы ─────────────────────────────────────────────────────────────────────────────
/** Цель соединения n8n: connections[«Имя»][тип][выход] = [{ node, type, index }]. */
export interface WorkflowConnTarget {
  node: string;
  type: string;
  index: number;
}

/** Узел n8n-workflow. Неизвестные поля n8n сохраняем как есть (канон без потерь). */
export interface WorkflowNode {
  id: string;
  name: string;
  type: string;
  typeVersion: number;
  position: [number, number];
  parameters: Record<string, unknown>;
  credentials?: Record<string, { id: string; name: string }>;
  webhookId?: string;
  disabled?: boolean;
  [k: string]: unknown;
}

/**
 * Авторское имя выхода ветвящегося узла — подпись ветки «для людей» на холсте/ленте/шеринге
 * (проба владельца 2026-07-09: «Вариант 1/2» вместо «Нажата кнопка»/«Написали боту»).
 * Switch v3 хранит его в rules.values[i].outputKey; для остальных типов имён нет.
 */
export function namedOutputLabel(
  node: { type: string; parameters?: Record<string, unknown> },
  outputIndex: number,
): string | undefined {
  if (node.type !== 'n8n-nodes-base.switch') return undefined;
  const rules = node.parameters?.rules;
  if (!rules || typeof rules !== 'object') return undefined;
  const values = (rules as Record<string, unknown>).values;
  if (!Array.isArray(values)) return undefined;
  const rule = values[outputIndex];
  if (!rule || typeof rule !== 'object') return undefined;
  const key = (rule as Record<string, unknown>).outputKey;
  return typeof key === 'string' && key.trim() !== '' ? key.trim() : undefined;
}

/**
 * ПОДПИСЬ MAIN-ВЫХОДА ветвящегося шага — ОДНА НА ВСЮ ПРОГРАММУ (26.08.2026). Ею подписаны
 * и чипы веток в ленте (lanes.ts → LaneBranchLabel), и пункты выбора «В какую ветку?» у
 * переноса шага через условие (moveWorkflowNode → need_branch). Разойдись эти два места —
 * человек выбирал бы ветку «Вариант 2», а на схеме она называлась бы иначе.
 * Виды: «Если» — да/нет по номеру выхода; остальные — вариант с авторским именем выхода
 * (Switch outputKey), если оно задано. Имена выходов из КАТАЛОГА (outNames) сюда не
 * доезжают — их подмешивает лента, у которой каталог под рукой.
 */
export type MainOutLabel =
  | { kind: 'yes' }
  | { kind: 'no' }
  | { kind: 'variant'; index: number; text?: string };

export function mainOutLabel(
  node: { type: string; parameters?: Record<string, unknown> },
  outputIndex: number,
): MainOutLabel {
  if (node.type === 'n8n-nodes-base.if') return { kind: outputIndex === 0 ? 'yes' : 'no' };
  const text = namedOutputLabel(node, outputIndex);
  return { kind: 'variant', index: outputIndex, ...(text ? { text } : {}) };
}

/** Соединения узла: main + любые другие типы портов (ai_tool, ai_languageModel, error…). */
export type WorkflowConnections = Record<
  string,
  Record<string, Array<Array<WorkflowConnTarget> | null>>
>;

export interface WorkflowJson {
  name?: string;
  nodes: WorkflowNode[];
  connections: WorkflowConnections;
  settings?: Record<string, unknown>;
  [k: string]: unknown;
}

// Санитарный потолок схемы (этап A2). 100 → 300 (SHARED-N8N Ф5: крупнейший шаблон
// ботов — 272 узла), 300 → 1000 (владелец 2026-07-28).
export const MAX_WORKFLOW_NODES = 1000;

/** Пустой канон нового черновика (создание по кнопке, backfill пустых лент). */
export function emptyWorkflow(name?: string): WorkflowJson {
  return {
    ...(name ? { name } : {}),
    nodes: [],
    connections: {},
    settings: { executionOrder: 'v1' },
  };
}

// ── Валидация формы (workflow приходит от МОДЕЛИ/клиента — не доверяем) ──────────────
export type WorkflowValidation =
  | { ok: true; workflow: WorkflowJson }
  | { ok: false; errors: string[] };

const isObj = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === 'object' && !Array.isArray(v);

export function validateWorkflow(value: unknown): WorkflowValidation {
  if (!isObj(value)) {
    return { ok: false, errors: ['workflow должен быть объектом { nodes: [...], connections: {...} }'] };
  }
  const errors: string[] = [];
  const wf = value as Record<string, unknown>;
  if (!Array.isArray(wf.nodes)) return { ok: false, errors: ['workflow.nodes должен быть массивом узлов'] };
  if (wf.nodes.length > MAX_WORKFLOW_NODES) {
    return { ok: false, errors: [`слишком много узлов (> ${MAX_WORKFLOW_NODES})`] };
  }
  if (wf.connections !== undefined && !isObj(wf.connections)) {
    errors.push('workflow.connections должен быть объектом { «Имя узла»: { main: [[...]] } }');
  }
  if (wf.settings !== undefined && !isObj(wf.settings)) errors.push('workflow.settings должен быть объектом');
  if (wf.name !== undefined && typeof wf.name !== 'string') errors.push('workflow.name должен быть строкой');

  const names = new Set<string>();
  const ids = new Set<string>();
  for (let i = 0; i < wf.nodes.length; i++) {
    const p = `nodes[${i}]`;
    const raw = wf.nodes[i];
    if (!isObj(raw)) {
      errors.push(`${p}: узел должен быть объектом`);
      continue;
    }
    const name = typeof raw.name === 'string' ? raw.name.trim() : '';
    if (!name) errors.push(`${p}.name: обязательная строка (уникальное имя узла)`);
    else if (names.has(name)) errors.push(`${p}.name: дубль имени узла «${name}» — имена уникальны`);
    else names.add(name);
    if (typeof raw.type !== 'string' || !raw.type.trim()) errors.push(`${p}.type: обязательная строка (полный тип узла)`);
    if (typeof raw.typeVersion !== 'number' || !Number.isFinite(raw.typeVersion)) {
      errors.push(`${p}.typeVersion: обязательное число`);
    }
    if (raw.parameters !== undefined && !isObj(raw.parameters)) {
      errors.push(`${p}.parameters: объект параметров (пусть пустой {})`);
    }
    if (raw.id !== undefined) {
      if (typeof raw.id !== 'string' || !raw.id.trim()) errors.push(`${p}.id: строка`);
      else if (ids.has(raw.id)) errors.push(`${p}.id: дубль id «${raw.id}»`);
      else ids.add(raw.id);
    }
    if (
      raw.position !== undefined &&
      !(Array.isArray(raw.position) && raw.position.length === 2 && raw.position.every((n) => typeof n === 'number'))
    ) {
      errors.push(`${p}.position: [x, y] числами`);
    }
  }

  if (isObj(wf.connections)) {
    for (const [from, byPort] of Object.entries(wf.connections)) {
      if (!names.has(from)) {
        errors.push(`connections: узла «${from}» нет в nodes (соединения адресуются ИМЕНАМИ узлов)`);
        continue;
      }
      if (!isObj(byPort)) {
        errors.push(`connections[«${from}»]: должен быть объектом { main: [[...]] }`);
        continue;
      }
      for (const [port, outputs] of Object.entries(byPort)) {
        if (!Array.isArray(outputs)) {
          errors.push(`connections[«${from}»].${port}: массив выходов (массив массивов целей)`);
          continue;
        }
        for (const targets of outputs) {
          if (targets === null) continue;
          if (!Array.isArray(targets)) {
            errors.push(`connections[«${from}»].${port}: каждый выход — массив целей [{ node, type, index }]`);
            continue;
          }
          for (const t of targets) {
            if (!isObj(t) || typeof t.node !== 'string') {
              errors.push(`connections[«${from}»].${port}: цель должна быть { node, type, index }`);
            } else if (!names.has(t.node)) {
              errors.push(`connections[«${from}»].${port}: цели «${t.node}» нет в nodes`);
            }
          }
        }
      }
    }
  }

  if (errors.length > 0) return { ok: false, errors };
  return { ok: true, workflow: value as unknown as WorkflowJson };
}

// ── Нормализация ─────────────────────────────────────────────────────────────────────
// Стабильный id узла без crypto (модуль чистый): хэш имени. Имена уникальны в workflow,
// а id стабилен между пересохранениями (webhookId от него — вебхук не перерегистрируется зря).
function nameHashId(name: string): string {
  let h = 5381;
  for (let i = 0; i < name.length; i++) h = ((h << 5) + h + name.charCodeAt(i)) | 0;
  return `node-${(h >>> 0).toString(36)}`;
}

/**
 * Канонический workflow: типы узлов → канон каталога, id/parameters/position/connections/
 * settings дозаполнены. Новый объект, без мутации.
 */
export function normalizeWorkflow(raw: WorkflowJson): WorkflowJson {
  const usedIds = new Set<string>(
    raw.nodes.map((n) => (typeof n.id === 'string' ? n.id : '')).filter(Boolean),
  );
  const nodes = raw.nodes.map((n, i) => {
    let id = typeof n.id === 'string' && n.id.trim() ? n.id : '';
    if (!id) {
      id = nameHashId(n.name);
      while (usedIds.has(id)) id = `${id}-${i}`;
      usedIds.add(id);
    }
    return {
      ...n,
      id,
      name: n.name.trim(),
      type: canonicalNodeType(n.type),
      parameters: isObj(n.parameters) ? n.parameters : {},
      position: (Array.isArray(n.position) ? n.position : [i % 4 === 0 ? 0 : (i % 4) * 360, Math.floor(i / 4) * 210]) as [
        number,
        number,
      ],
    };
  });
  return {
    ...raw,
    ...(typeof raw.name === 'string' && raw.name.trim() ? { name: raw.name.trim() } : {}),
    nodes,
    connections: isObj(raw.connections) ? raw.connections : {},
    settings: { executionOrder: 'v1', ...(isObj(raw.settings) ? raw.settings : {}) },
  };
}

/** Все типы узлов workflow — для выборки из node_catalog. */
export function workflowNodeTypes(wf: WorkflowJson): Set<string> {
  return new Set(wf.nodes.map((n) => n.type));
}

// ── Вебхуки ──────────────────────────────────────────────────────────────────────────
const SAFE_WEBHOOK_ID = /^[A-Za-z0-9_-]+$/;

/**
 * Явный ASCII webhookId у webhook-триггеров (без него n8n строит путь из ИМЕНИ узла:
 * кириллица → percent-encoded URL → 404, апдейты Telegram теряются; прод 74lq 2026-07-05).
 * Уже валидный webhookId сохраняем (не перерегистрировать зря), кривой/пустой →
 * детерминированный <seed>-<nodeId>. requiredPrefix (общий инстанс, SHARED-N8N Ф2):
 * валидный, но БЕЗ приставки клиента webhookId тоже перегенерируется — на общем сервере
 * каждый вебхук-путь обязан начинаться с `<userId>_` (по нему держится забор).
 */
export function ensureWebhookIds(wf: WorkflowJson, seed: string, requiredPrefix?: string): WorkflowJson {
  return {
    ...wf,
    nodes: wf.nodes.map((n) => {
      if (!isWebhookNodeType(n.type)) return n;
      if (
        typeof n.webhookId === 'string' &&
        SAFE_WEBHOOK_ID.test(n.webhookId) &&
        (!requiredPrefix || n.webhookId.startsWith(requiredPrefix))
      ) {
        return n;
      }
      return { ...n, webhookId: webhookIdFor(n.id, seed) };
    }),
  };
}

/** Есть ли в workflow webhook-триггер (telegram/webhook/form/chat…) среди включённых узлов. */
export function workflowHasWebhookTrigger(wf: WorkflowJson): boolean {
  return wf.nodes.some((n) => n.disabled !== true && isWebhookNodeType(n.type));
}

/** Тип триггера «Форма» мини-аппа (пакет n8n-nodes-scriptera-ai в каталоге — CUSTOM.*). */
export const FORM_TRIGGER_TYPE = 'CUSTOM.scripteraForm';

/** Тип узла «Экран» мини-аппа (страница по ходу сценария, HOW-IT-WORKS §15). */
export const SCREEN_NODE_TYPE = 'CUSTOM.scripteraScreen';

/** Параметры-коллекции экрана — главный интерфейс шага, НЕ «Продвинутое»
 *  (ParamsForm.mainNames; сами поля описаны в пакете узлов — lib/screen-params). */
export const SCREEN_MAIN_PARAMS = ['image', 'video', 'fields', 'buttons'];

/**
 * Вебхук-адрес триггера «Форма» в готовом к деплою workflow (после ensureWebhookIds):
 * путь узла — '={{ $webhookId }}' с isFullPath, т.е. на инстансе форма живёт на
 * /webhook/<webhookId>. Его пишем в form_tokens.webhook_path (HOW-IT-WORKS §15).
 * null — включённого триггера «Форма» в сценарии нет (токен гасим, не удаляем).
 */
export function formTriggerWebhookPath(wf: WorkflowJson): string | null {
  const node = wf.nodes.find((n) => n.disabled !== true && n.type === FORM_TRIGGER_TYPE);
  return node && typeof node.webhookId === 'string' && node.webhookId ? node.webhookId : null;
}

/** Тип чат-триггера (штатный узел n8n; стандарт сборки — docs/HOW-IT-WORKS.md §16). */
export const CHAT_TRIGGER_TYPE = '@n8n/n8n-nodes-langchain.chatTrigger';

// ИИ-Агент и RespondToWebhook — единственные узлы, шлющие чанки в streaming-чате
// (разведка живьём на n8n 2.28.6, docs/HOW-IT-WORKS.md §16); RespondToWebhook стримит
// только с typeVersion ≥ 1.5 — старее отдаёт 200 с пустым телом (находка смока).
const AGENT_NODE_TYPE = '@n8n/n8n-nodes-langchain.agent';
const RESPOND_TO_WEBHOOK_TYPE = 'n8n-nodes-base.respondToWebhook';
const RESPOND_STREAMING_MIN_VERSION = 1.5;

/**
 * Вебхук-адрес чат-триггера в готовом к деплою workflow (после ensureWebhookIds):
 * на инстансе чат живёт на /webhook/<webhookId>/chat (суффикс /chat — протокол узла).
 * webhookId пишем в chat_tokens.webhook_path (docs/HOW-IT-WORKS.md §16).
 * null — включённого чат-триггера в сценарии нет (токен гасим, не удаляем).
 */
export function chatTriggerWebhookPath(wf: WorkflowJson): string | null {
  const node = wf.nodes.find((n) => n.disabled !== true && n.type === CHAT_TRIGGER_TYPE);
  return node && typeof node.webhookId === 'string' && node.webhookId ? node.webhookId : null;
}

/**
 * Первый экран формы из параметров триггера «Форма»: структурные поля экрана
 * (HOW-IT-WORKS §15, сборка+валидация — screenFromNodeParams; легаси json-параметр
 * screen поддержан там же). Снимок кладётся в form_tokens.screen при деплое —
 * открытие ссылки отдаёт его статично, без инстанса. null — формы нет или экран
 * собран неверно (мусор в статичную отдачу не едет).
 */
export function formTriggerScreen(wf: WorkflowJson): unknown {
  const node = wf.nodes.find((n) => n.disabled !== true && n.type === FORM_TRIGGER_TYPE);
  if (!node) return null;
  return screenFromNodeParams(node.parameters ?? {}).screen ?? null;
}

// Правило «один бот = один активный сценарий» (этап A12) СНЯТО диспетчером Ф5
// (SCRIPTS-MODE-2): много активных сценариев на одном телеграм-боте — норма, апдейты
// маршрутизирует наш приёмник (@scriptera/db bot-dispatch). Хелперы
// findTelegramTriggerConflicts/telegramTriggerCredIds удалены вместе с правилом.

// ── Смоук после деплоя (этап A5 — правда после включения) ───────────────────────────
export interface DeploySmokeState {
  /** Workflow, перечитанный с инстанса ПОСЛЕ активации (getWorkflowRaw); null = не прочитался. */
  raw: Record<string, unknown> | null;
  /** Ошибка активации из n8n (getActivationError) — триггер/вебхук не поднялся. */
  activationError: string | null;
}

// n8n INodeIssues: { typeUnknown?, parameters?/credentials?/input?: { [key]: string[] } }.
function nodeIssueTexts(node: Record<string, unknown>): string[] {
  const issues = node.issues;
  if (!issues || typeof issues !== 'object' || Array.isArray(issues)) return [];
  const rec = issues as Record<string, unknown>;
  const out: string[] = [];
  if (rec.typeUnknown === true) out.push('неизвестный тип узла — сервер его не знает');
  for (const group of ['parameters', 'credentials', 'input']) {
    const g = rec[group];
    if (!g || typeof g !== 'object' || Array.isArray(g)) continue;
    for (const msgs of Object.values(g as Record<string, unknown>)) {
      if (!Array.isArray(msgs)) continue;
      for (const m of msgs) if (typeof m === 'string' && m.trim()) out.push(m.trim());
    }
  }
  return out;
}

/**
 * Разбор правды после включения сценария: пустой список = n8n подтвердил активацию.
 * Для webhook-триггеров это и есть «вебхук зарегистрирован»: n8n регистрирует вебхук
 * в момент активации, а неудачу кладёт в activationError. Тексты — материал для
 * самопочинки бота и системной строки чата (не для прямого показа в UI-баннере).
 */
export function deploySmokeProblems(state: DeploySmokeState): string[] {
  const problems: string[] = [];
  if (!state.raw) {
    problems.push('не удалось перечитать сценарий с сервера после включения — состояние неизвестно');
  } else {
    if (state.raw.active !== true) problems.push('n8n не включил сценарий (active=false)');
    const nodes = Array.isArray(state.raw.nodes) ? state.raw.nodes : [];
    for (const n of nodes) {
      if (!n || typeof n !== 'object' || Array.isArray(n)) continue;
      const node = n as Record<string, unknown>;
      const name = typeof node.name === 'string' && node.name ? node.name : '?';
      for (const t of nodeIssueTexts(node)) problems.push(`шаг «${name}»: ${t}`);
    }
  }
  if (state.activationError) problems.push(`ошибка активации: ${state.activationError.slice(0, 300)}`);
  return problems;
}

// ── Контакты ─────────────────────────────────────────────────────────────────────────
/** Шаг «Контакты» — наша база подписчиков (SCRIPTS-MODE-2 Ф6.3). */
export const CONTACTS_NODE_TYPE = 'CUSTOM.scripteraContacts';

/**
 * Шаги ВЫЛОЖЕННОЙ схемы, у которых обязательный доступ НЕ ПРИВЯЗАН к узлу (в `credentials`
 * узла нет ключа этого типа). Отличие от workflowMissingCreds: там вопрос «есть ли у
 * человека такой доступ вообще», тут — «стоит ли он на узле». n8n 2.x при включении
 * отбивает workflow с непривязанным обязательным доступом (validateForActivation), и
 * тумблер падал «Бот не включился» у бота, собранного до починки сборки (19.09.2026:
 * шаги Instagram без facebookGraphApi). Тумблер по этому списку сначала перевыкладывает
 * схему с живыми доступами (pushDeployedScenario) и только потом включает.
 */
export function workflowUnboundCreds(wf: WorkflowJson, catalog: Map<string, CatalogNodeInfo>): MissingCred[] {
  const unbound: MissingCred[] = [];
  for (const node of wf.nodes) {
    if (node.disabled === true) continue;
    const info = catalog.get(node.type);
    const credTypes = info ? stepCredTypes(info, node.parameters ?? {}) : [];
    for (const credType of credTypes) {
      if (!node.credentials?.[credType]?.id) unbound.push({ stepId: node.id, title: node.name, credType });
    }
  }
  return unbound;
}

/**
 * Шаги «Контакты» в схеме (включённые). Нужны пре-флайту запуска: узел ходит за данными
 * во внутренний канал «шлюз → web», и если канал не настроен на сервере, шаг падает 503
 * у ПЕРВОГО посетителя. Проверка до включения честнее падения в лицо (прод 2026-07-31:
 * на seapi не были заданы WEB_INTERNAL_URL/WEB_INTERNAL_TOKEN — форма отдала 500).
 * Выключенные шаги пропускаем — правило то же, что у workflowMissingCreds.
 */
export function workflowContactsSteps(wf: WorkflowJson): Array<{ stepId: string; title: string }> {
  return wf.nodes
    .filter((n) => n.disabled !== true && n.type === CONTACTS_NODE_TYPE)
    .map((n) => ({ stepId: n.id, title: n.name }));
}

// ── Креды ────────────────────────────────────────────────────────────────────────────
/** Недостающие доступы по узлам workflow (полный список — не по ленте-отрисовке). */
export function workflowMissingCreds(
  wf: WorkflowJson,
  catalog: Map<string, CatalogNodeInfo>,
  availableCredTypes: ReadonlySet<string>,
): MissingCred[] {
  const missing: MissingCred[] = [];
  for (const node of wf.nodes) {
    if (node.disabled === true) continue;
    const info = catalog.get(node.type);
    // Шагу может быть нужно НЕСКОЛЬКО доступов сразу (stepCredTypes) — просим все, иначе
    // недостающая Робокасса у шага «Оплата» молчала бы, а сценарий падал у покупателя.
    const credTypes = info ? stepCredTypes(info, node.parameters ?? {}) : [];
    for (const credType of credTypes) {
      if (!availableCredTypes.has(credType)) missing.push({ stepId: node.id, title: node.name, credType });
    }
  }
  return missing;
}

/**
 * Привязать аккаунты инстанса к узлам workflow (новый объект): тип креда — из каталога
 * по параметрам узла, значение — ВЫБРАННЫЙ на узле аккаунт, а при его отсутствии первый
 * кред этого типа на инстансе.
 *
 * INVARIANT(scriptera): выбранный на узле аккаунт (node.credentials[credType].id, живой
 * на инстансе) НЕ перезаписывать. Причина: раньше `bound` шёл последним в спреде и
 * затирал выбор — при двух ботах одного типа сценарий молча уезжал на первого по списку
 * сервера, а кабинет показывал другого (заказ владельца 2026-07-31). Молчаливый первый
 * остаётся только там, где выбирать не из чего: единственный доступ типа, служебные
 * креды Scriptera. Канон: docs/MINIMAL-PRODUCT-PLAN.md К34, botAccountState (bot-account.ts).
 */
export function bindWorkflowCredentials(
  wf: WorkflowJson,
  catalog: Map<string, CatalogNodeInfo>,
  creds: InstanceCredRef[],
): WorkflowJson {
  const byType = new Map<string, InstanceCredRef>();
  for (const c of creds) if (!byType.has(c.type)) byType.set(c.type, c);
  // Спрятанные двойники учётки (twins) — ЖИВЫЕ креды: список их не показывает, но узел
  // уже собранного бота стоит именно на таком, и подменять его каноническим нельзя
  // (у работающего бота сменился бы кред). Канон: bot-account.ts, hideTwinCreds.
  const byId = new Map<string, InstanceCredRef>();
  for (const c of creds) {
    byId.set(c.id, c);
    for (const twin of c.twins ?? []) if (!byId.has(twin)) byId.set(twin, c);
  }
  return {
    ...wf,
    nodes: wf.nodes.map((node) => {
      const info = catalog.get(node.type);
      // Все доступы шага, а не один: узлу «Оплата» нужны и Робокасса, и ключ Scriptera —
      // привязав один (или, как было, ни одного), мы отправляли на сервер заведомо мёртвый шаг.
      const credTypes = info ? stepCredTypes(info, node.parameters ?? {}) : [];
      const prev = node.credentials as Record<string, { id?: unknown } | undefined> | undefined;
      const bound: Record<string, { id: string; name: string }> = {};
      for (const credType of credTypes) {
        const pickedId = typeof prev?.[credType]?.id === 'string' ? (prev[credType]!.id as string) : '';
        // Выбор уважаем, только если он ЖИВОЙ и того же типа: мёртвый id (удалили доступ,
        // переезд инстанса) валит забор общего сервера — такой заменяем первым по типу,
        // а честный отказ «выберите заново» даёт botAccountState выше по течению.
        const picked = pickedId ? byId.get(pickedId) : undefined;
        const keep = !!picked && picked.type === credType;
        const cred = keep ? picked : byType.get(credType);
        // keep — оставляем ИМЕННО выбранный id (у двойника он свой, а имя учётки общее:
        // переподключение кладёт всем кредам учётки одно имя и один свежий ключ).
        if (cred) bound[credType] = { id: keep ? pickedId : cred.id, name: cred.name };
      }
      if (Object.keys(bound).length === 0) return node;
      return { ...node, credentials: { ...(node.credentials ?? {}), ...bound } };
    }),
  };
}

// ── Переименование узла (правки из кабинета: имя узла = ключ connections) ────────────
// Выражения других узлов ссылаются на шаг ПО ИМЕНИ — $('Имя'), $node["Имя"], $items("Имя")
// (как в редакторе n8n, который при переименовании переписывает и их). Без этого
// переименование шага в кабинете тихо ломало чужие выражения (BUILDER-REPAIR-PLAN этап 3).
const escapeRegExp = (s: string): string => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

function renameNodeRefsInValue(value: unknown, oldName: string, newName: string): unknown {
  if (typeof value === 'string') {
    if (!value.includes(oldName)) return value;
    const esc = escapeRegExp(oldName);
    return value
      .replace(new RegExp(`\\$\\(\\s*'${esc}'\\s*\\)`, 'g'), `$('${newName}')`)
      .replace(new RegExp(`\\$\\(\\s*"${esc}"\\s*\\)`, 'g'), `$("${newName}")`)
      .replace(new RegExp(`\\$node\\[\\s*'${esc}'\\s*\\]`, 'g'), `$node['${newName}']`)
      .replace(new RegExp(`\\$node\\[\\s*"${esc}"\\s*\\]`, 'g'), `$node["${newName}"]`)
      .replace(new RegExp(`\\$items\\(\\s*'${esc}'`, 'g'), `$items('${newName}'`)
      .replace(new RegExp(`\\$items\\(\\s*"${esc}"`, 'g'), `$items("${newName}"`);
  }
  if (Array.isArray(value)) return value.map((v) => renameNodeRefsInValue(v, oldName, newName));
  if (isObj(value)) {
    return Object.fromEntries(
      Object.entries(value).map(([k, v]) => [k, renameNodeRefsInValue(v, oldName, newName)]),
    );
  }
  return value;
}

export function renameWorkflowNode(wf: WorkflowJson, oldName: string, newName: string): WorkflowJson {
  const name = newName.trim();
  if (!name || name === oldName || wf.nodes.some((n) => n.name === name)) return wf;
  const connections: WorkflowConnections = {};
  for (const [from, byPort] of Object.entries(wf.connections)) {
    const nextByPort: WorkflowConnections[string] = {};
    for (const [port, outputs] of Object.entries(byPort)) {
      nextByPort[port] = outputs.map(
        (targets) => targets?.map((t) => (t.node === oldName ? { ...t, node: name } : t)) ?? null,
      );
    }
    connections[from === oldName ? name : from] = nextByPort;
  }
  return {
    ...wf,
    nodes: wf.nodes.map((n) => ({
      ...(n.name === oldName ? { ...n, name } : n),
      parameters: renameNodeRefsInValue(n.parameters ?? {}, oldName, name) as Record<
        string,
        unknown
      >,
    })),
    connections,
  };
}

// ── Правка одного узла из конструктора (этап B2: панель настроек на холсте) ──────────
export interface NodeEditPatch {
  /** Новое имя узла (переименование тянет connections — см. renameWorkflowNode). */
  name?: string;
  /** Полная замена parameters узла (форма шлёт целиком, как n8n-редактор). */
  parameters?: Record<string, unknown>;
  /** Полная замена credentials узла; пустой объект — убрать привязки. */
  credentials?: Record<string, { id: string; name: string }>;
  /** Вкл/выкл шага (SCHEMA-EDITOR-PARITY этап 3): false снимает флаг с узла (канон
   *  n8n хранит только disabled: true, выключенный узел не исполняется). */
  disabled?: boolean;
  /** Поведение при ошибке шага: 'continueErrorOutput' добавляет узлу последний
   *  main-выход «если ошибка» (SCHEMA-EDITOR-PARITY этап 3); 'continueRegularOutput'
   *  (Ф38) — n8n кладёт ошибку в обычный выход и сценарий идёт дальше; null — как в
   *  n8n по умолчанию: шаг падает, сценарий останавливается. Уход С 'continueErrorOutput'
   *  снимает связи error-выхода (иначе осиротевшие рёбра стали бы фантомным fan-out). */
  onError?: 'continueErrorOutput' | 'continueRegularOutput' | null;
  /** Повтор при ошибке (Ф38, n8n retryOnFail/maxTries/waitBetweenTries): «HTTP с 3
   *  попытками». null — выключить (флаги снимаются с узла). Клампы — как в UI n8n:
   *  tries 2–5, waitMs 0–5000. */
  retry?: { tries: number; waitMs: number } | null;
  /** Заметка шага (SCHEMA-EDITOR-PARITY этап 3): trim + кап 300 (как structuralWorkflow),
   *  пустая строка снимает заметку с узла. */
  notes?: string;
}

export type NodeEditResult =
  | { ok: true; workflow: WorkflowJson }
  | { ok: false; error: 'node_not_found' | 'name_taken' };

/**
 * Применить правку узла по id (новый объект, без мутации). Имя-дубль — честная ошибка
 * (имя узла = ключ connections, молчаливый no-op прятал бы потерю правки).
 */
export function editWorkflowNode(wf: WorkflowJson, nodeId: string, patch: NodeEditPatch): NodeEditResult {
  const node = wf.nodes.find((n) => n.id === nodeId);
  if (!node) return { ok: false, error: 'node_not_found' };
  const name = patch.name?.trim();
  if (name && name !== node.name && wf.nodes.some((n) => n.name === name)) {
    return { ok: false, error: 'name_taken' };
  }

  let out: WorkflowJson = {
    ...wf,
    nodes: wf.nodes.map((n) => {
      if (n.id !== nodeId) return n;
      const {
        credentials: prevCreds,
        disabled: prevDisabled,
        onError: prevOnError,
        notes: prevNotes,
        retryOnFail: prevRetry,
        maxTries: prevTries,
        waitBetweenTries: prevWait,
        ...rest
      } = n;
      const nextCreds = patch.credentials === undefined ? prevCreds : patch.credentials;
      const nextDisabled = patch.disabled === undefined ? prevDisabled === true : patch.disabled;
      const nextOnError = patch.onError === undefined ? prevOnError : (patch.onError ?? undefined);
      const nextNotes = patch.notes === undefined ? prevNotes : patch.notes.trim().slice(0, 300) || undefined;
      // Повтор при ошибке (Ф38): пишем поля n8n; null снимает все три флага с узла.
      const prevRetryFields =
        prevRetry === true
          ? {
              retryOnFail: true,
              ...(prevTries !== undefined ? { maxTries: prevTries } : {}),
              ...(prevWait !== undefined ? { waitBetweenTries: prevWait } : {}),
            }
          : {};
      const nextRetryFields =
        patch.retry === undefined
          ? prevRetryFields
          : patch.retry === null
            ? {}
            : {
                retryOnFail: true,
                maxTries: Math.max(2, Math.min(5, Math.round(patch.retry.tries))),
                waitBetweenTries: Math.max(0, Math.min(5000, Math.round(patch.retry.waitMs))),
              };
      return {
        ...rest,
        ...(nextDisabled ? { disabled: true } : {}),
        ...(nextOnError !== undefined ? { onError: nextOnError } : {}),
        ...nextRetryFields,
        ...(nextNotes !== undefined ? { notes: nextNotes } : {}),
        ...(nextCreds && Object.keys(nextCreds).length > 0 ? { credentials: nextCreds } : {}),
        ...(patch.parameters !== undefined ? { parameters: patch.parameters } : {}),
      } as WorkflowNode;
    }),
  };
  // Уход с error-выхода (в null или continueRegularOutput) снимает его связи: error —
  // всегда ПОСЛЕДНИЙ main-выход (workflowPorts по ИСХОДНОМУ канону), без чистки рёбра
  // пересчитались бы как fan-out штатного выхода.
  if (patch.onError !== undefined && patch.onError !== 'continueErrorOutput' && node.onError === 'continueErrorOutput') {
    const byPort = out.connections[node.name];
    const outputs = byPort?.main;
    const ports = workflowPorts(wf)[node.id];
    const errIdx = ports?.outputs[ports.outputs.length - 1]?.isError ? ports.outputs.length - 1 : -1;
    if (byPort && Array.isArray(outputs) && errIdx >= 0 && outputs.length > errIdx) {
      out = {
        ...out,
        connections: { ...out.connections, [node.name]: { ...byPort, main: outputs.slice(0, errIdx) } },
      };
    }
  }
  if (name && name !== node.name) out = renameWorkflowNode(out, node.name, name);
  return { ok: true, workflow: out };
}

// ── Структурные правки из конструктора (этап B3: холст = полноценный конструктор) ────
// Все операции — чистые (новый объект), адресуют узлы по id (имена меняются
// переименованием), результат остаётся валидным каноном без отдельного validateWorkflow.

const MAX_PORT_INDEX = 20; // разумный потолок выходов/входов узла (n8n столько не рисует)

/** Уникальное имя узла: занятое достраивается « 2», « 3»… (имя узла = ключ connections). */
export function uniqueNodeName(wf: WorkflowJson, wanted: string): string {
  const base = wanted.trim() || 'Шаг';
  const taken = new Set(wf.nodes.map((n) => n.name));
  if (!taken.has(base)) return base;
  for (let i = 2; ; i++) {
    const name = `${base} ${i}`;
    if (!taken.has(name)) return name;
  }
}

export interface AddNodeInput {
  type: string;
  typeVersion: number;
  /** Желаемое имя (displayName каталога / имя helper-сервиса) — при занятости достроится. */
  name: string;
  position: [number, number];
  /** Стартовые параметры поверх дефолтов ADD_NODE_PARAMS (пикер моделей Scriptera AI
   *  предзаполняет resource/model — доктрина docs/concepts/SCRIPTERA-AI-DOCTRINE.md). */
  params?: Record<string, unknown>;
}

// Стартовые параметры добавляемого узла там, где дефолт n8n нам не подходит.
// lmChatOpenAi: n8n включает Responses API по умолчанию, но через кред «Scriptera AI
// (модели)» (наш шлюз) этот режим не работает — шлюз говорит /chat/completions.
// Модель: n8n-дефолт gpt-5-mini у шлюза отсутствует → наша дефолтная (как в шаблонах;
// gpt-5.1 выключен из user_models 2026-07-11 — дефолт живой моделью).
const ADD_NODE_PARAMS: Record<string, Record<string, unknown>> = {
  '@n8n/n8n-nodes-langchain.lmChatOpenAi': {
    responsesApiEnabled: false,
    model: { __rl: true, mode: 'list', value: 'gpt-5.6-luna', cachedResultName: 'gpt-5.6-luna' },
  },
};

/** Добавить узел (без связей — их тянут на холсте). Возвращает id нового узла. */
export function addWorkflowNode(
  wf: WorkflowJson,
  input: AddNodeInput,
): { workflow: WorkflowJson; nodeId: string; name: string } {
  const name = uniqueNodeName(wf, input.name);
  const withNode = normalizeWorkflow({
    ...wf,
    nodes: [
      ...wf.nodes,
      {
        id: '',
        name,
        type: input.type,
        typeVersion: input.typeVersion,
        position: input.position,
        parameters: { ...(ADD_NODE_PARAMS[input.type] ?? {}), ...(input.params ?? {}) },
      },
    ],
  });
  const node = withNode.nodes.find((n) => n.name === name);
  return { workflow: withNode, nodeId: node?.id ?? '', name };
}

export type DuplicateNodeResult =
  | { ok: true; workflow: WorkflowJson; nodeId: string; name: string }
  | { ok: false; error: 'node_not_found' };

export interface DuplicateNodeOptions {
  /**
   * КОПИЯ ВСТАЁТ СЛЕДОМ ЗА ОРИГИНАЛОМ В ТОЙ ЖЕ ВЕТКЕ (владелец 26.08.2026): A→B
   * становится A→A2→B. Без флага — прежнее поведение (копия без связей, её тянут мышью
   * на холсте), оно и остаётся у старых мест вызова.
   */
  after?: boolean;
}

/**
 * Дублировать шаг (SCHEMA-EDITOR-PARITY этап 3): копия типа+параметров (и привязок
 * доступа); имя достраивается « 2», позиция — под оригиналом со сдвигом.
 * Служебные флаги (disabled/onError/notes/webhookId) не копируются: заметка и ветка
 * ошибки — про место шага в схеме, webhookId у копии должен быть свой (ensureWebhookIds).
 *
 * СВЯЗИ (владелец 26.08.2026, `after`): копия ВРЕЗАЕТСЯ следом за оригиналом по ПЕРВОМУ
 * main-выходу — тому же, что несёт ветку «1»/«да» у развилки. Правило одно на все виды
 * шагов: выхода нет вовсе (шаг не ветвится и никуда не ведёт) — копия просто цепляется за
 * оригинал (A→A2); первый выход занят одной целью — копия встаёт между ними; занят
 * несколькими (fan-out) — врезаться некуда, копия остаётся без связей, как раньше.
 */
export function duplicateWorkflowNode(
  wf: WorkflowJson,
  nodeId: string,
  opts?: DuplicateNodeOptions,
): DuplicateNodeResult {
  const node = wf.nodes.find((n) => n.id === nodeId);
  if (!node) return { ok: false, error: 'node_not_found' };
  const name = uniqueNodeName(wf, node.name);
  const withNode = normalizeWorkflow({
    ...wf,
    nodes: [
      ...wf.nodes,
      {
        id: '',
        name,
        type: node.type,
        typeVersion: node.typeVersion,
        position: [node.position[0] + 40, node.position[1] + 40] as [number, number],
        // Глубокая копия: правка параметров копии не должна трогать оригинал.
        parameters: JSON.parse(JSON.stringify(node.parameters ?? {})) as Record<string, unknown>,
        ...(node.credentials ? { credentials: JSON.parse(JSON.stringify(node.credentials)) as Record<string, { id: string; name: string }> } : {}),
      },
    ],
  });
  const added = withNode.nodes.find((n) => n.name === name);
  const copyId = added?.id ?? '';
  if (!opts?.after || !copyId) return { ok: true, workflow: withNode, nodeId: copyId, name };

  // Первый main-выход оригинала: у развилки это ветка «1»/«да» — так решил владелец,
  // потому что «после развилки» однозначного места нет вовсе. Error-выход не в счёт.
  const first = (withNode.connections[node.name]?.main ?? [])[0] ?? [];
  const targets = first.filter((t) => t.node !== name);
  let cur = withNode;
  const step = (r: WorkflowEdgeResult): boolean => {
    if (!r.ok) return false;
    cur = r.workflow;
    return true;
  };
  // Врезка в fan-out не определена (какая из целей «следующий шаг»?) — копия остаётся
  // без связей, как при обычном дублировании.
  if (targets.length > 1) return { ok: true, workflow: withNode, nodeId: copyId, name };
  const byName = new Map(withNode.nodes.map((n) => [n.name, n]));
  for (const t of targets) {
    const tgt = byName.get(t.node);
    if (!tgt) continue;
    if (
      !step(disconnectWorkflowNodes(cur, { sourceId: nodeId, port: 'main', sourceIndex: 0, targetId: tgt.id, targetIndex: t.index ?? 0 })) ||
      !step(connectWorkflowNodes(cur, { sourceId: copyId, port: 'main', sourceIndex: 0, targetId: tgt.id, targetIndex: t.index ?? 0 }))
    ) {
      return { ok: true, workflow: withNode, nodeId: copyId, name };
    }
  }
  if (!step(connectWorkflowNodes(cur, { sourceId: nodeId, port: 'main', sourceIndex: 0, targetId: copyId, targetIndex: 0 }))) {
    return { ok: true, workflow: withNode, nodeId: copyId, name };
  }
  return { ok: true, workflow: cur, nodeId: copyId, name };
}

/**
 * Удалить узел по id вместе со всеми его связями (исходящими и входящими).
 * Линейный узел (ровно одно входящее main-ребро и ровно одна main-цель на выходе)
 * при удалении СШИВАЕТСЯ: A→X→B становится A→B — иначе цепочка распадается на куски,
 * которые в вертикальной ленте нечем соединить (скрин владельца 2026-07-08).
 * Развилки/fan-out/error-выходы не сшиваем: непонятно, какую ветку считать «той самой».
 */
export function removeWorkflowNode(wf: WorkflowJson, nodeId: string): NodeEditResult {
  const node = wf.nodes.find((n) => n.id === nodeId);
  if (!node) return { ok: false, error: 'node_not_found' };

  // Входящие main-рёбра в удаляемый узел: (источник, индекс выхода, позиция в списке целей).
  const incoming: Array<{ from: string; outputIndex: number }> = [];
  for (const [from, byPort] of Object.entries(wf.connections)) {
    if (from === node.name) continue;
    (byPort.main ?? []).forEach((targets, outputIndex) => {
      for (const t of targets ?? []) {
        if (t.node === node.name) incoming.push({ from, outputIndex });
      }
    });
  }
  // Исходящие main-цели удаляемого узла (по всем выходам: у развилки их несколько).
  const outgoing: WorkflowConnTarget[] = (wf.connections[node.name]?.main ?? []).flatMap(
    (targets) => targets ?? [],
  );
  const splice = incoming.length === 1 && outgoing.length === 1 ? { in: incoming[0]!, out: outgoing[0]! } : null;

  const connections: WorkflowConnections = {};
  for (const [from, byPort] of Object.entries(wf.connections)) {
    if (from === node.name) continue;
    const nextByPort: WorkflowConnections[string] = {};
    for (const [port, outputs] of Object.entries(byPort)) {
      nextByPort[port] = outputs.map((targets, oi) => {
        const kept = targets?.filter((t) => t.node !== node.name) ?? null;
        // Сшивание: на месте ребра «источник→X» появляется «источник→B» (если его ещё нет).
        if (splice && port === 'main' && from === splice.in.from && oi === splice.in.outputIndex) {
          const next = kept ?? [];
          return next.some((t) => t.node === splice.out.node && t.index === splice.out.index)
            ? next
            : [...next, { ...splice.out }];
        }
        return kept;
      });
    }
    connections[from] = nextByPort;
  }
  return {
    ok: true,
    workflow: { ...wf, nodes: wf.nodes.filter((n) => n.id !== nodeId), connections },
  };
}

export interface ReplaceNodeInput {
  type: string;
  typeVersion: number;
  /** Желаемое новое имя (displayName нового типа); занятое достроится « 2». */
  name: string;
  /** Стартовые параметры нового типа (Ф16+Ф17: режим гнезда у режимных довесков). */
  params?: Record<string, unknown>;
}

/**
 * Заменить тип узла (этап C1: «Заменить шаг» из настроек): id, позиция и связи
 * сохраняются; параметры, аккаунты и webhookId сбрасываются — у нового типа своя форма
 * (params задаёт стартовые значения нового типа, не перенос старых).
 */
export function replaceWorkflowNode(
  wf: WorkflowJson,
  nodeId: string,
  input: ReplaceNodeInput,
): NodeEditResult {
  const node = wf.nodes.find((n) => n.id === nodeId);
  if (!node) return { ok: false, error: 'node_not_found' };
  const swapped: WorkflowJson = {
    ...wf,
    nodes: wf.nodes.map((n) => {
      if (n.id !== nodeId) return n;
      const { credentials: _creds, webhookId: _hook, ...rest } = n;
      return {
        ...rest,
        type: input.type,
        typeVersion: input.typeVersion,
        parameters: input.params ?? {},
      } as WorkflowNode;
    }),
  };
  const wanted = input.name.trim();
  if (!wanted || wanted === node.name) return { ok: true, workflow: swapped };
  return { ok: true, workflow: renameWorkflowNode(swapped, node.name, uniqueNodeName(swapped, wanted)) };
}

export interface CondPillsInput {
  /** Каким шагом карточка живёт после правки: «Если» (одна пилюля) или развилка (много). */
  type: string;
  typeVersion: number;
  /** Готовые параметры карточки (их считает chat-cond.ts → writeChatSwitch). */
  parameters: Record<string, unknown>;
  /**
   * Куда переезжает каждая ЛИНИЯ карточки: outMap[старый выход] = новый выход, -1 — линии
   * этого выхода уходят вместе со своей пилюлей. Без переезда молчаливо соврала бы вся
   * карточка: у развилки «не соответствует условиям» — ПОСЛЕДНИЙ выход, и он сдвигается
   * от каждой добавленной пилюли.
   */
  outMap: number[];
}

export type CondPillsResult =
  | { ok: true; workflow: WorkflowJson }
  | { ok: false; error: 'node_not_found' | 'bad_port' };

/**
 * Правка ПИЛЮЛЬ карточки «Условие» (У12): параметры + тип шага + переезд линий одним
 * ходом. Отдельная операция нужна ровно из-за линий: добавили пилюлю — «Не соответствует
 * условиям» уехало на выход ниже, убрали — все линии под ней поднялись, а первая вторая
 * пилюля вдобавок превращает «Если» в развилку. Два отдельных хода оставляли бы схему в
 * состоянии, где линия ведёт не из той ветки, и человек увидел бы это как поломку бота.
 */
export function condPillsWorkflow(
  wf: WorkflowJson,
  nodeId: string,
  input: CondPillsInput,
): CondPillsResult {
  const node = wf.nodes.find((n) => n.id === nodeId);
  if (!node) return { ok: false, error: 'node_not_found' };
  if (
    input.outMap.some(
      (i) => !Number.isInteger(i) || i < -1 || i > MAX_PORT_INDEX,
    ) ||
    input.outMap.length > MAX_PORT_INDEX + 1
  ) {
    return { ok: false, error: 'bad_port' };
  }
  const nodes = wf.nodes.map((n) =>
    n.id === nodeId
      ? ({ ...n, type: input.type, typeVersion: input.typeVersion, parameters: input.parameters } as WorkflowNode)
      : n,
  );
  const byPort = wf.connections?.[node.name];
  const main = byPort?.main;
  if (!byPort || !Array.isArray(main)) return { ok: true, workflow: { ...wf, nodes } };
  const moved: Array<Array<WorkflowConnTarget> | null> = [];
  main.forEach((targets, i) => {
    const to = input.outMap[i] ?? i;
    if (to < 0 || !targets) return;
    while (moved.length <= to) moved.push(null);
    moved[to] = [...(moved[to] ?? []), ...targets];
  });
  return {
    ok: true,
    workflow: {
      ...wf,
      nodes,
      connections: { ...wf.connections, [node.name]: { ...byPort, main: moved } },
    },
  };
}

/**
 * Копия workflow без привязок аккаунтов: снимаем credentials со всех узлов. Перенос схемы
 * другому пользователю (claim публичной ссылки) — доступы он подставит свои; параметры-логика
 * остаются как есть. Иммутабельно: новый объект, исходный не трогаем.
 */
export function stripWorkflowCredentials(wf: WorkflowJson): WorkflowJson {
  return {
    ...wf,
    nodes: wf.nodes.map((n) => {
      if (!n.credentials) return n;
      const { credentials: _drop, ...rest } = n;
      return rest as WorkflowNode;
    }),
  };
}

/**
 * Только СТРУКТУРА workflow для публичного снимка: имена/типы/позиции узлов + связи +
 * служебные флаги отрисовки (disabled, onError, notes). parameters → {}, credentials/webhookId
 * убраны. Этого хватает холсту и ленте кабинета, чтобы нарисовать схему 1:1, но НИ значения
 * настроек, ни ключи в анонимный браузер не уходят (см. INVARIANT в share-model.ts).
 */
export function structuralWorkflow(wf: WorkflowJson): WorkflowJson {
  return {
    name: wf.name,
    nodes: wf.nodes.map((n) => ({
      id: n.id,
      name: n.name,
      type: n.type,
      typeVersion: n.typeVersion,
      position: n.position,
      parameters: {},
      ...(n.disabled === true ? { disabled: true } : {}),
      ...(n.onError ? { onError: n.onError } : {}),
      ...(typeof n.notes === 'string' && n.notes.trim() ? { notes: n.notes.trim().slice(0, 300) } : {}),
    })) as WorkflowNode[],
    connections: wf.connections ?? {},
    settings: {},
  };
}

export interface WorkflowEdgeRef {
  /** id узла-источника / узла-цели (id стабильны, имена — нет). */
  sourceId: string;
  /** Порт источника: 'main' | 'ai_tool' | … (конструктор рисует только main). */
  port: string;
  /** Индекс выхода источника (ветки If/Switch, error-выход — последний main). */
  sourceIndex: number;
  targetId: string;
  /** Индекс входа цели (Merge принимает в несколько входов). */
  targetIndex: number;
}

export type WorkflowEdgeResult =
  | { ok: true; workflow: WorkflowJson }
  | { ok: false; error: 'node_not_found' | 'self_connection' | 'bad_port' };

const edgeRefValid = (e: WorkflowEdgeRef): boolean =>
  Number.isInteger(e.sourceIndex) &&
  e.sourceIndex >= 0 &&
  e.sourceIndex <= MAX_PORT_INDEX &&
  Number.isInteger(e.targetIndex) &&
  e.targetIndex >= 0 &&
  e.targetIndex <= MAX_PORT_INDEX &&
  !!e.port.trim();

/** Протянуть связь. Уже существующая точно такая же — ok без изменений (идемпотентно). */
export function connectWorkflowNodes(wf: WorkflowJson, edge: WorkflowEdgeRef): WorkflowEdgeResult {
  if (!edgeRefValid(edge)) return { ok: false, error: 'bad_port' };
  if (edge.sourceId === edge.targetId) return { ok: false, error: 'self_connection' };
  const source = wf.nodes.find((n) => n.id === edge.sourceId);
  const target = wf.nodes.find((n) => n.id === edge.targetId);
  if (!source || !target) return { ok: false, error: 'node_not_found' };

  const byPort = { ...(wf.connections[source.name] ?? {}) };
  const outputs = [...(byPort[edge.port] ?? [])];
  while (outputs.length <= edge.sourceIndex) outputs.push(null);
  const targets = [...(outputs[edge.sourceIndex] ?? [])];
  if (targets.some((t) => t.node === target.name && (t.index ?? 0) === edge.targetIndex)) {
    return { ok: true, workflow: wf };
  }
  targets.push({ node: target.name, type: edge.port, index: edge.targetIndex });
  outputs[edge.sourceIndex] = targets;
  return {
    ok: true,
    workflow: {
      ...wf,
      connections: { ...wf.connections, [source.name]: { ...byPort, [edge.port]: outputs } },
    },
  };
}

/** Убрать связь; пустые хвосты выходов/портов подчищаются (не копить мусорные [null]). */
export function disconnectWorkflowNodes(wf: WorkflowJson, edge: WorkflowEdgeRef): WorkflowEdgeResult {
  const source = wf.nodes.find((n) => n.id === edge.sourceId);
  const target = wf.nodes.find((n) => n.id === edge.targetId);
  if (!source || !target) return { ok: false, error: 'node_not_found' };

  const byPort = { ...(wf.connections[source.name] ?? {}) };
  const outputs = [...(byPort[edge.port] ?? [])];
  const prev = outputs[edge.sourceIndex];
  if (!prev) return { ok: true, workflow: wf };
  const targets = prev.filter((t) => !(t.node === target.name && (t.index ?? 0) === edge.targetIndex));
  outputs[edge.sourceIndex] = targets.length > 0 ? targets : null;
  while (outputs.length > 0 && (outputs[outputs.length - 1] == null || outputs[outputs.length - 1]!.length === 0)) {
    outputs.pop();
  }
  if (outputs.length > 0) byPort[edge.port] = outputs;
  else delete byPort[edge.port];

  const connections = { ...wf.connections };
  if (Object.keys(byPort).length > 0) connections[source.name] = byPort;
  else delete connections[source.name];
  return { ok: true, workflow: { ...wf, connections } };
}

export interface InsertNodeInput {
  type: string;
  typeVersion: number;
  /** Желаемое имя нового шага (displayName каталога); занятое достроится « 2». */
  name: string;
  position: [number, number];
  /** Выход, в который врезаемся: source[port][sourceIndex]. */
  sourceId: string;
  port: string;
  sourceIndex: number;
  /** Задано — вставка В существующее ребро (A→новый→B); иначе — A→новый на свободный выход. */
  targetId?: string;
  targetIndex?: number;
  /** Стартовые параметры нового шага (см. AddNodeInput.params). */
  params?: Record<string, unknown>;
}

export type InsertNodeResult =
  | { ok: true; workflow: WorkflowJson; nodeId: string; name: string }
  | { ok: false; error: 'node_not_found' | 'self_connection' | 'bad_port' };

/**
 * Вставить новый шаг В МЕСТО связи (SCHEMA-EDITOR этап 4: «+» на ребре/свободном выходе).
 * Ребро A→B становится A→новый→B; свободный выход A становится A→новый. Соединение A→новый
 * держит ИСХОДНЫЙ порт/индекс выхода источника (error-выход остаётся error-выходом), выход
 * нового шага — всегда main[0]. Атомарно: один результат = одна ревизия канона.
 */
export function insertWorkflowNode(wf: WorkflowJson, input: InsertNodeInput): InsertNodeResult {
  const source = wf.nodes.find((n) => n.id === input.sourceId);
  if (!source) return { ok: false, error: 'node_not_found' };
  if (input.targetId) {
    if (input.targetId === input.sourceId) return { ok: false, error: 'self_connection' };
    if (!wf.nodes.some((n) => n.id === input.targetId)) return { ok: false, error: 'node_not_found' };
  }

  const added = addWorkflowNode(wf, {
    type: input.type,
    typeVersion: input.typeVersion,
    name: input.name,
    position: input.position,
    params: input.params,
  });
  const newId = added.nodeId;

  // Разрываем старое ребро A→B (если врезаемся в существующую связь), затем A→новый→B.
  let cur = added.workflow;
  if (input.targetId != null && input.targetIndex != null) {
    const cut = disconnectWorkflowNodes(cur, {
      sourceId: input.sourceId,
      port: input.port,
      sourceIndex: input.sourceIndex,
      targetId: input.targetId,
      targetIndex: input.targetIndex,
    });
    if (!cut.ok) return cut;
    cur = cut.workflow;
  }
  const toNew = connectWorkflowNodes(cur, {
    sourceId: input.sourceId,
    port: input.port,
    sourceIndex: input.sourceIndex,
    targetId: newId,
    targetIndex: 0,
  });
  if (!toNew.ok) return toNew;
  cur = toNew.workflow;
  if (input.targetId != null && input.targetIndex != null) {
    const fromNew = connectWorkflowNodes(cur, {
      sourceId: newId,
      port: 'main',
      sourceIndex: 0,
      targetId: input.targetId,
      targetIndex: input.targetIndex,
    });
    if (!fromNew.ok) return fromNew;
    cur = fromNew.workflow;
  }
  return { ok: true, workflow: cur, nodeId: newId, name: added.name };
}

export interface AttachNodeInput {
  type: string;
  typeVersion: number;
  /** Желаемое имя нового довеска (displayName каталога); занятое достроится « 2». */
  name: string;
  /** Шаг с гнездом, к которому пристёгиваем. */
  targetId: string;
  /** ai_*-гнездо цели (ai_languageModel, ai_memory, ai_tool, ai_embedding…). */
  port: string;
  /** Стартовые параметры довеска (см. AddNodeInput.params). */
  params?: Record<string, unknown>;
}

// self_connection здесь недостижим (источник — свежесозданный узел), но тип соединения общий.
export type AttachNodeResult =
  | { ok: true; workflow: WorkflowJson; nodeId: string; name: string }
  | { ok: false; error: 'node_not_found' | 'self_connection' | 'bad_port' };

/**
 * Пристегнуть НОВЫЙ довесок (модель/память/инструмент…) к гнезду шага
 * (SCHEMA-EDITOR-PARITY этап 2). В каноне n8n довесок — ИСТОЧНИК связи:
 * connections[довесок][ai_*][0] → цель. Узел встаёт под целью; каждый уже висящий
 * довесок сдвигает новый вправо. Подходит ли ТИП довеска гнезду — проверяет вызывающий
 * по каталогу (ports_json), здесь только структура. Атомарно: узел + связь = одна ревизия.
 */
export function attachWorkflowNode(wf: WorkflowJson, input: AttachNodeInput): AttachNodeResult {
  if (!input.port.startsWith('ai_')) return { ok: false, error: 'bad_port' };
  const target = wf.nodes.find((n) => n.id === input.targetId);
  if (!target) return { ok: false, error: 'node_not_found' };

  // Сколько довесков уже висит на цели (все ai_*-порты) — сдвиг нового вправо.
  let attached = 0;
  for (const byPort of Object.values(wf.connections)) {
    for (const [port, outputs] of Object.entries(byPort)) {
      if (!port.startsWith('ai_')) continue;
      for (const targets of outputs) {
        for (const t of targets ?? []) if (t.node === target.name) attached++;
      }
    }
  }

  const added = addWorkflowNode(wf, {
    type: input.type,
    typeVersion: input.typeVersion,
    name: input.name,
    position: [target.position[0] + attached * 180, target.position[1] + 220],
    params: input.params,
  });
  const linked = connectWorkflowNodes(added.workflow, {
    sourceId: added.nodeId,
    port: input.port,
    sourceIndex: 0,
    targetId: input.targetId,
    targetIndex: 0,
  });
  if (!linked.ok) return linked;
  return { ok: true, workflow: linked.workflow, nodeId: added.nodeId, name: added.name };
}

/**
 * Довески, пристёгнутые к шагу (SCHEMA-EDITOR-PARITY этап 2, форма шага): источники
 * ai_*-рёбер, целящих в узел. Порядок — гнездо, затем имя (как чипы ленты). wf —
 * нормализованный канон (id узлов заполнены).
 */
export function workflowAiAttachments(
  wf: WorkflowJson,
  nodeId: string,
): Array<{ id: string; name: string; port: string }> {
  const target = wf.nodes.find((n) => n.id === nodeId);
  if (!target) return [];
  const byName = new Map(wf.nodes.map((n) => [n.name, n]));
  const out: Array<{ id: string; name: string; port: string }> = [];
  for (const [srcName, byPort] of Object.entries(wf.connections)) {
    const src = byName.get(srcName);
    if (!src) continue;
    for (const [port, outputs] of Object.entries(byPort)) {
      if (!port.startsWith('ai_')) continue;
      for (const targets of outputs) {
        for (const t of targets ?? []) {
          if (t.node === target.name) out.push({ id: src.id, name: src.name, port });
        }
      }
    }
  }
  return out.sort((a, b) => a.port.localeCompare(b.port) || a.name.localeCompare(b.name));
}

/** Обратное к workflowAiAttachments: для узла-довеска — его РОДИТЕЛЬ и порт-гнездо
 *  (ai_*), в который он вложен. null — узел не довесок (нет исходящей ai_*-связи). Нужно,
 *  чтобы «заменить» довесок вело в attach-выдачу его порта, а не в общий пикер (D1). */
export function workflowAiParent(
  wf: WorkflowJson,
  nodeId: string,
): { id: string; name: string; port: string } | null {
  const child = wf.nodes.find((n) => n.id === nodeId);
  if (!child) return null;
  const byName = new Map(wf.nodes.map((n) => [n.name, n]));
  const byPort = wf.connections[child.name];
  if (!byPort) return null;
  for (const [port, outputs] of Object.entries(byPort)) {
    if (!port.startsWith('ai_')) continue;
    for (const targets of outputs) {
      for (const t of targets ?? []) {
        const parent = byName.get(t.node);
        if (parent) return { id: parent.id, name: parent.name, port };
      }
    }
  }
  return null;
}

/**
 * ВХОДЯЩАЯ СВЯЗЬ ШАГА — та самая «связь сверху» (владелец 26.08.2026): её рвёт пункт меню
 * карточки. Связь ровно одна — иначе рвать нечего или непонятно что (шаг с несколькими
 * входами живёт в шторке и рисуется повторами, у корня дорожки входа нет вовсе). Лента
 * берёт тот же адрес из своей раскладки (`step.entry`), холст — отсюда.
 */
export function workflowEntryEdge(wf: WorkflowJson, nodeId: string): WorkflowEdgeRef | null {
  const incoming = workflowDiagram(wf)
    .edges.filter((e) => e.port === 'main' && e.target === nodeId)
    .map((e) => ({
      sourceId: e.source,
      port: e.port,
      sourceIndex: e.outputIndex,
      targetId: e.target,
      targetIndex: e.targetIndex,
    }));
  return incoming.length === 1 ? incoming[0]! : null;
}

/** Переставить узлы (drag на холсте): id → [x, y]; неизвестные id игнорируются. */
export function setWorkflowNodePositions(
  wf: WorkflowJson,
  positions: Record<string, [number, number]>,
): WorkflowJson {
  return {
    ...wf,
    nodes: wf.nodes.map((n) => {
      const p = positions[n.id];
      return p && p.every((v) => Number.isFinite(v)) ? { ...n, position: [p[0], p[1]] as [number, number] } : n;
    }),
  };
}

// ── Перенос шага ЧЕРЕЗ УСЛОВИЕ (владелец 26.08.2026) ─────────────────────────────────
// Две картинки, обе описаны владельцем дословно:
//  • X стоит НАД условием C (X→C): «вниз» = «в ветку условия». X снимается со своего места
//    (сшивка A→C) и встаёт ПЕРВЫМ шагом выбранной ветки: C[выход k]→X→прежний первый шаг.
//    Веток у условия несколько — номер выхода приходит параметром `into`; не пришёл —
//    отвечаем `need_branch` со списком веток, и выбор показывает интерфейс.
//  • X — ПЕРВЫЙ шаг ветки условия C (C[k]→X): «вверх» = «перед условием, общим для всех
//    веток». X снимается из ветки (C[k]→его следующий) и врезается перед C (A→X→C).
// Позиции на холсте не трогаем: там шаги стоят там, где их поставили руками, и «правильного»
// места у переехавшего шага нет — раскладку холста человек ведёт сам.
function moveAcrossCondition(
  wf: WorkflowJson,
  nodeId: string,
  dir: 'up' | 'down',
  main: DiagramEdge[],
  opts?: MoveNodeOptions,
): MoveNodeResult | null {
  const cannot = { ok: false as const, error: 'cannot_move' as const };
  const inOf = (id: string) => main.filter((e) => e.target === id);
  const dataOut = (id: string) => main.filter((e) => e.source === id && !e.isError);
  const errOut = (id: string) => main.filter((e) => e.source === id && e.isError);
  const byId = new Map(wf.nodes.map((n) => [n.id, n]));
  const ports = workflowPorts(wf);
  const outCount = (id: string): number => ports[id]?.outputs.filter((o) => !o.isError).length ?? 0;
  // Шаг, который переезжает, обязан быть ЛИНЕЙНЫМ: у развилки «встать в ветку» значило бы
  // выбрать ещё и её собственный выход — второго выбора владелец не заказывал.
  const linear = (id: string): boolean =>
    outCount(id) === 1 && errOut(id).length === 0 && byId.get(id)?.type !== LOOP_NODE_TYPE;

  if (dir === 'down') {
    const outs = dataOut(nodeId);
    const edgeToCond = outs[0];
    if (outs.length !== 1 || !edgeToCond) return null;
    const condId = edgeToCond.target;
    const cond = byId.get(condId);
    // Не развилка — обычная пара соседей, её обменивает старый путь.
    if (!cond || outCount(condId) < 2) return null;
    if (!linear(nodeId) || cond.type === LOOP_NODE_TYPE) return cannot;
    // Слияние: в условие приходят ещё откуда-то — вынуть X из-под него нельзя.
    if (inOf(condId).length !== 1) return cannot;
    const ins = inOf(nodeId);
    if (ins.length > 1) return cannot;
    if (ins.some((e) => e.targetIndex !== 0 || e.source === condId)) return cannot;
    // Ветки, куда шаг ВПРАВДУ встанет: пустая (шаг станет единственным) или с одним первым
    // шагом. Fan-out внутри ветки — «первого шага» у неё нет, такую ветку не предлагаем.
    const options: MoveBranchOption[] = [];
    for (let k = 0; k < outCount(condId); k += 1) {
      const targets = main.filter((e) => e.source === condId && !e.isError && e.outputIndex === k);
      options.push({ outputIndex: k, label: mainOutLabel(cond, k), usable: targets.length <= 1 });
    }
    if (!options.some((o) => o.usable)) return cannot;
    const k = opts?.into?.outputIndex;
    if (k === undefined) return { ok: false, error: 'need_branch', condId, branches: options };
    if (!options.some((o) => o.outputIndex === k && o.usable)) return cannot;
    const targets = main.filter((e) => e.source === condId && !e.isError && e.outputIndex === k);

    let cur: WorkflowJson = wf;
    const step = (r: WorkflowEdgeResult): boolean => {
      if (!r.ok) return false;
      cur = r.workflow;
      return true;
    };
    // 1. Снять шаг со своего места: A→X→C становится A→C (порт входа сохраняется).
    if (!step(disconnectWorkflowNodes(cur, { sourceId: nodeId, port: 'main', sourceIndex: edgeToCond.outputIndex, targetId: condId, targetIndex: edgeToCond.targetIndex }))) return cannot;
    for (const e of ins) {
      if (
        !step(disconnectWorkflowNodes(cur, { sourceId: e.source, port: e.port, sourceIndex: e.outputIndex, targetId: nodeId, targetIndex: 0 })) ||
        !step(connectWorkflowNodes(cur, { sourceId: e.source, port: e.port, sourceIndex: e.outputIndex, targetId: condId, targetIndex: 0 }))
      ) {
        return cannot;
      }
    }
    // 2. Поставить его первым шагом ветки: C[k]→X→прежний первый шаг ветки.
    for (const t of targets) {
      if (
        !step(disconnectWorkflowNodes(cur, { sourceId: condId, port: 'main', sourceIndex: k, targetId: t.target, targetIndex: t.targetIndex })) ||
        !step(connectWorkflowNodes(cur, { sourceId: nodeId, port: 'main', sourceIndex: edgeToCond.outputIndex, targetId: t.target, targetIndex: t.targetIndex }))
      ) {
        return cannot;
      }
    }
    if (!step(connectWorkflowNodes(cur, { sourceId: condId, port: 'main', sourceIndex: k, targetId: nodeId, targetIndex: 0 }))) return cannot;
    return { ok: true, workflow: cur };
  }

  // dir === 'up': шаг — первый в ветке условия, поднимаем его ПЕРЕД условием.
  const ins = inOf(nodeId);
  const fromCond = ins[0];
  if (ins.length !== 1 || !fromCond || fromCond.isError) return null;
  const condId = fromCond.source;
  const cond = byId.get(condId);
  if (!cond || outCount(condId) < 2) return null;
  if (!linear(nodeId) || cond.type === LOOP_NODE_TYPE) return cannot;
  if (fromCond.targetIndex !== 0) return cannot;
  // Ветка ведёт ещё куда-то (fan-out) — «первого шага» у неё нет.
  if (main.filter((e) => e.source === condId && !e.isError && e.outputIndex === fromCond.outputIndex).length !== 1) return cannot;
  const outs = dataOut(nodeId);
  const next = outs[0];
  if (outs.length > 1) return cannot;
  if (next && next.target === condId) return cannot; // возврат в само условие — цикл
  const condIns = inOf(condId);
  // Условие — начало дорожки: вставать перед ним некуда (то же правило, что у обмена).
  if (condIns.length === 0) return cannot;
  if (condIns.some((e) => e.targetIndex !== 0 || e.source === nodeId)) return cannot;

  let cur: WorkflowJson = wf;
  const step = (r: WorkflowEdgeResult): boolean => {
    if (!r.ok) return false;
    cur = r.workflow;
    return true;
  };
  // 1. Вынуть шаг из ветки: C[k]→X→Y становится C[k]→Y.
  if (!step(disconnectWorkflowNodes(cur, { sourceId: condId, port: 'main', sourceIndex: fromCond.outputIndex, targetId: nodeId, targetIndex: 0 }))) return cannot;
  if (next) {
    if (
      !step(disconnectWorkflowNodes(cur, { sourceId: nodeId, port: 'main', sourceIndex: next.outputIndex, targetId: next.target, targetIndex: next.targetIndex })) ||
      !step(connectWorkflowNodes(cur, { sourceId: condId, port: 'main', sourceIndex: fromCond.outputIndex, targetId: next.target, targetIndex: next.targetIndex }))
    ) {
      return cannot;
    }
  }
  // 2. Врезать его перед условием: A→X→C (порт входа условия сохраняется).
  for (const e of condIns) {
    if (
      !step(disconnectWorkflowNodes(cur, { sourceId: e.source, port: e.port, sourceIndex: e.outputIndex, targetId: condId, targetIndex: 0 })) ||
      !step(connectWorkflowNodes(cur, { sourceId: e.source, port: e.port, sourceIndex: e.outputIndex, targetId: nodeId, targetIndex: 0 }))
    ) {
      return cannot;
    }
  }
  if (!step(connectWorkflowNodes(cur, { sourceId: nodeId, port: 'main', sourceIndex: next?.outputIndex ?? 0, targetId: condId, targetIndex: 0 }))) return cannot;
  return { ok: true, workflow: cur };
}

// ── Перестановка соседних шагов (VERTICAL-SCHEMA-PLAN этап 4: «вверх/вниз» из ленты) ──
// Меняет местами два СОСЕДНИХ линейных шага одной дорожки перецепкой рёбер:
// X→A→B→Y становится X→B→A→Y (позиции узлов тоже меняются местами — холст не путается).
// Через развилку (Если/Switch), цикл, fan-out и слияние НЕ прыгаем — перестановка живёт
// строго «в пределах своей ветки»; экзотика остаётся холсту (перецепка линий).

const LOOP_NODE_TYPE = 'n8n-nodes-base.splitInBatches';

/** Ветка условия в выборе «В какую ветку?»: номер выхода + та же подпись, что у её чипа.
 *  Отдаём ВСЕ выходы условия (иначе интерфейс не смог бы честно их пронумеровать), а
 *  `usable` говорит, встанет ли шаг в эту ветку: у ветки с fan-out «первого шага» нет. */
export interface MoveBranchOption {
  outputIndex: number;
  label: MainOutLabel;
  usable: boolean;
}

export type MoveNodeResult =
  | { ok: true; workflow: WorkflowJson }
  | { ok: false; error: 'node_not_found' | 'cannot_move' }
  /** Шаг уходит В УСЛОВИЕ, а веток у него несколько — какую взять, решает человек.
   *  `condId` — само условие: по нему интерфейс подписывает ветки, как их чипы. */
  | { ok: false; error: 'need_branch'; condId: string; branches: MoveBranchOption[] };

export interface MoveNodeOptions {
  /** Ответ на `need_branch`: в какую ветку условия ставить шаг первым. */
  into?: { outputIndex: number };
}

export function moveWorkflowNode(
  wf: WorkflowJson,
  nodeId: string,
  dir: 'up' | 'down',
  opts?: MoveNodeOptions,
): MoveNodeResult {
  if (!wf.nodes.some((n) => n.id === nodeId)) return { ok: false, error: 'node_not_found' };
  const cannot = { ok: false as const, error: 'cannot_move' as const };
  const main = workflowDiagram(wf).edges.filter((e) => e.port === 'main');
  const inOf = (id: string) => main.filter((e) => e.target === id);
  const dataOut = (id: string) => main.filter((e) => e.source === id && !e.isError);

  // ── ПЕРЕНОС ЧЕРЕЗ УСЛОВИЕ (владелец 26.08.2026) ───────────────────────────────────
  // Обмен соседями (ниже) через развилку не ходит и никогда не ходил: «какую ветку считать
  // той самой» — не наше дело. Владелец назвал ответ словами: «вниз» у шага НАД условием
  // значит «в ветку условия» (ветку выбирает человек), «вверх» у ПЕРВОГО шага ветки —
  // «перед условием, общим для всех веток». Всё остальное (цикл, fan-out, error-выход,
  // слияние) по-прежнему нельзя.
  const branchMove = moveAcrossCondition(wf, nodeId, dir, main, opts);
  if (branchMove) return branchMove;

  // Пара соседей A→B (A выше): «вверх» — nodeId и его сосед сверху, «вниз» — то же
  // движение, увиденное от соседа снизу. Вход по error-ребру = nodeId — голова ветки
  // ошибки: выдернуть его «вверх» из неё нельзя.
  let aId: string;
  let bId: string;
  if (dir === 'up') {
    const entry = inOf(nodeId)[0];
    if (!entry || inOf(nodeId).length !== 1 || entry.isError) return cannot;
    aId = entry.source;
    bId = nodeId;
  } else {
    const next = dataOut(nodeId)[0];
    if (!next || dataOut(nodeId).length !== 1) return cannot;
    aId = nodeId;
    bId = next.target;
  }

  const a = wf.nodes.find((n) => n.id === aId);
  const b = wf.nodes.find((n) => n.id === bId);
  if (!a || !b) return cannot;
  if (a.type === LOOP_NODE_TYPE || b.type === LOOP_NODE_TYPE) return cannot;
  // Оба шага линейные: ровно один data-выход (Если/Switch отпадают ещё здесь).
  const ports = workflowPorts(wf);
  const singleDataOutput = (id: string): boolean =>
    (ports[id]?.outputs.filter((o) => !o.isError).length ?? 0) === 1;
  if (!singleDataOutput(aId) || !singleDataOutput(bId)) return cannot;

  const aIns = inOf(aId);
  if (aIns.length === 0) return cannot; // A — начало дорожки (триггер/корень), выше не встать
  // Входы A перецепляются на B «как есть» — только в обычный первый вход (Merge не наш кейс);
  // вход из самого B (обратное ребро B→A) дал бы петлю на себя.
  if (aIns.some((e) => e.targetIndex !== 0 || e.source === bId)) return cannot;
  const ab = dataOut(aId)[0];
  if (!ab || dataOut(aId).length !== 1 || ab.target !== bId || ab.targetIndex !== 0) return cannot;
  if (inOf(bId).length !== 1) return cannot; // в B сливается что-то ещё — не цепочка
  const bOuts = dataOut(bId);
  if (bOuts.length > 1) return cannot; // fan-out после B — параллельные ветки, не цепочка
  const y = bOuts[0]; // может отсутствовать (B — хвост дорожки)
  if (y && y.target === aId) return cannot;

  // Перецепка готовыми операциями канона: X→A становится X→B (порт/выход источника
  // сохраняются — error-выход остаётся error-выходом), затем A→B рвётся, Y переходит
  // к A (его вход-индекс сохраняется — Merge-цели живут), B→A замыкает цепочку.
  let cur: WorkflowJson = wf;
  const step = (r: WorkflowEdgeResult): boolean => {
    if (!r.ok) return false;
    cur = r.workflow;
    return true;
  };
  for (const e of aIns) {
    if (
      !step(disconnectWorkflowNodes(cur, { sourceId: e.source, port: e.port, sourceIndex: e.outputIndex, targetId: aId, targetIndex: 0 })) ||
      !step(connectWorkflowNodes(cur, { sourceId: e.source, port: e.port, sourceIndex: e.outputIndex, targetId: bId, targetIndex: 0 }))
    ) {
      return cannot;
    }
  }
  if (!step(disconnectWorkflowNodes(cur, { sourceId: aId, port: 'main', sourceIndex: ab.outputIndex, targetId: bId, targetIndex: 0 }))) return cannot;
  if (y) {
    if (
      !step(disconnectWorkflowNodes(cur, { sourceId: bId, port: 'main', sourceIndex: y.outputIndex, targetId: y.target, targetIndex: y.targetIndex })) ||
      !step(connectWorkflowNodes(cur, { sourceId: aId, port: 'main', sourceIndex: ab.outputIndex, targetId: y.target, targetIndex: y.targetIndex }))
    ) {
      return cannot;
    }
  }
  if (!step(connectWorkflowNodes(cur, { sourceId: bId, port: 'main', sourceIndex: y?.outputIndex ?? 0, targetId: aId, targetIndex: 0 }))) return cannot;

  return {
    ok: true,
    workflow: {
      ...cur,
      nodes: cur.nodes.map((n) =>
        n.id === aId
          ? { ...n, position: [b.position[0], b.position[1]] as [number, number] }
          : n.id === bId
            ? { ...n, position: [a.position[0], a.position[1]] as [number, number] }
            : n,
      ),
    },
  };
}

// ── Дифф-правки канона (этап A10: scenario_save изменениями, не полным JSON) ─────────
// Гранулярность — узел целиком: модель переписывает только изменённые узлы, а не весь
// workflow (полный JSON на каждое сохранение = ~⅔ выходных токенов сложного заказа).

export interface WorkflowChanges {
  /** Добавить или заменить узел ЦЕЛИКОМ (матч по name; замена не трогает связи узла). */
  upsertNodes?: WorkflowNode[];
  /** Имена удаляемых узлов; их связи (входящие и исходящие) убираются автоматически. */
  removeNodes?: string[];
  /** Заменить ВСЕ исходящие связи узла (значение null — убрать все исходящие). */
  setConnections?: Record<string, WorkflowConnections[string] | null>;
}

export type WorkflowChangesResult =
  | { ok: true; workflow: WorkflowJson }
  | { ok: false; errors: string[] };

const CHANGES_KEYS = new Set(['upsertNodes', 'removeNodes', 'setConnections']);

/**
 * Применить дифф к канону. Вход rawChanges — от модели, форме не доверяем; сомнение
 * (неизвестное поле, несуществующий узел, пустой дифф) = отказ с внятной ошибкой,
 * вызывающий код требует полный workflow. Результат обязан пройти validateWorkflow.
 */
export function applyWorkflowChanges(base: WorkflowJson, rawChanges: unknown): WorkflowChangesResult {
  if (!isObj(rawChanges)) {
    return { ok: false, errors: ['changes должен быть объектом { upsertNodes?, removeNodes?, setConnections? }'] };
  }
  const errors: string[] = [];
  const unknownKeys = Object.keys(rawChanges).filter((k) => !CHANGES_KEYS.has(k));
  if (unknownKeys.length > 0) {
    errors.push(
      `changes: неизвестные поля ${unknownKeys.map((k) => `«${k}»`).join(', ')} — доступны только ` +
        'upsertNodes / removeNodes / setConnections; для остальных правок пришли полный workflow',
    );
  }
  const upserts = rawChanges.upsertNodes === undefined ? [] : rawChanges.upsertNodes;
  if (!Array.isArray(upserts) || upserts.some((n) => !isObj(n) || typeof n.name !== 'string' || !n.name.trim())) {
    errors.push('changes.upsertNodes: массив узлов workflow (у каждого обязателен name)');
  }
  const removes = rawChanges.removeNodes === undefined ? [] : rawChanges.removeNodes;
  if (!Array.isArray(removes) || removes.some((r) => typeof r !== 'string' || !r.trim())) {
    errors.push('changes.removeNodes: массив имён узлов (строки)');
  }
  const setConns = rawChanges.setConnections === undefined ? {} : rawChanges.setConnections;
  if (!isObj(setConns) || Object.values(setConns).some((v) => v !== null && !isObj(v))) {
    errors.push('changes.setConnections: объект { «Имя узла»: { main: [[…]] } | null }');
  }
  if (errors.length > 0) return { ok: false, errors };

  const upsertNodes = upserts as WorkflowNode[];
  const removeNames = new Set((removes as string[]).map((r) => r.trim()));
  const connChanges = setConns as Record<string, unknown>;
  if (upsertNodes.length === 0 && removeNames.size === 0 && Object.keys(connChanges).length === 0) {
    return { ok: false, errors: ['changes пуст — нечего применять'] };
  }

  const baseNames = new Set(base.nodes.map((n) => n.name));
  for (const name of removeNames) {
    if (!baseNames.has(name)) {
      errors.push(`removeNodes: узла «${name}» нет в текущем workflow — перечитай scenario_get`);
    }
  }
  if (errors.length > 0) return { ok: false, errors };

  // 1. Удаления: узел + его исходящие связи + все цели, указывающие на него.
  let nodes = base.nodes.filter((n) => !removeNames.has(n.name));
  const connections: WorkflowConnections = {};
  for (const [from, byPort] of Object.entries(base.connections)) {
    if (removeNames.has(from)) continue;
    const nextByPort: WorkflowConnections[string] = {};
    for (const [port, outputs] of Object.entries(byPort)) {
      nextByPort[port] = outputs.map(
        (targets) => targets?.filter((t) => !removeNames.has(t.node)) ?? null,
      );
    }
    connections[from] = nextByPort;
  }

  // 2. Upsert: замена по name на месте, новые — в конец.
  for (const node of upsertNodes) {
    const name = node.name.trim();
    const idx = nodes.findIndex((n) => n.name === name);
    if (idx >= 0) nodes = [...nodes.slice(0, idx), { ...node, name }, ...nodes.slice(idx + 1)];
    else nodes = [...nodes, { ...node, name }];
  }

  // 3. Связи: полная замена исходящих для указанных узлов (null — убрать).
  const namesAfter = new Set(nodes.map((n) => n.name));
  for (const [from, val] of Object.entries(connChanges)) {
    if (!namesAfter.has(from)) {
      errors.push(`setConnections: узла «${from}» нет в workflow после правок — проверь имя`);
      continue;
    }
    if (val === null) delete connections[from];
    else connections[from] = val as WorkflowConnections[string];
  }
  if (errors.length > 0) return { ok: false, errors };

  return { ok: true, workflow: { ...base, nodes, connections } };
}

// ── Линт n8n-JSON (этап A3 — docs/archive/BUILDER-QUALITY-SYNC-PLAN.md) ──────────────────────
// Проверяет то, к чему был слеп базовый линт A2 (приёмка 2026-07-05): пустой required
// с default (updates=[] у telegramTrigger), недопустимые пары дискриминаторов
// (resource=chat + operation=sendPhoto), скрытые displayOptions-параметры (file при
// binaryData=true), params.model узлов Scriptera по живому списку шлюза, связность графа.
// Данные — node_catalog.params_json (сырые INodeProperties: дубли имён с разными
// displayOptions сохранены — «operation» у telegram описан отдельно на каждый resource).

/** Модель шлюза для проверки params.model (структурно = EnabledModelInfo из @scriptera/db). */
export interface ScripteraModelInfo {
  name: string;
  kind: string | null;
  caps: { edit: boolean; minInputImages: number; maxInputImages: number } | null;
}

export interface WorkflowLintOptions {
  /** Живой список открытых юзеру моделей; null/undefined — проверка model пропускается. */
  models?: ScripteraModelInfo[] | null;
  /** Базы отпечатков ОСТАЛЬНЫХ сценариев клиента (BUILDER-UX 4.2): та же база другой
   *  embedding-моделью = тихая поломка поиска; null/undefined — кросс-сверки нет. */
  embeddings?: KnownEmbeddingUsage[] | null;
  /** Каналы БОТА этого сценария (Б3) — против них сверяется шаг «Проверка подписки».
   *  null/undefined — сверки нет (сборщик, тесты): молчим, а не врём ошибкой. */
  tgChannels?: readonly LintTgChannel[] | null;
  /** Подключённые доступы клиента (Ф0, BOT-INTEGRATIONS): против них сверяется аккаунт
   *  шага запуска Telegram/Instagram — не выбран из нескольких или выбранный удалён =
   *  ОШИБКА (схема неисправна до перевыбора). null/undefined — сверки нет (сборщик,
   *  тесты): списка доступов тут не знают, молчим, а не врём ошибкой. */
  accountCreds?: readonly InstanceCredRef[] | null;
}

/** Канал бота глазами линта: адрес (как он пишется в шаг) и можно ли им проверять подписку. */
export interface LintTgChannel {
  /** '@логин' публичного канала либо числовой chat_id приватного. */
  address: string;
  /** Бот в канале администратор — только тогда getChatMember отдаёт чужой статус. */
  canCheck: boolean;
}

// Выражение n8n проверит рантайм — статически не оцениваем.
export const isExpression = (v: unknown): boolean =>
  typeof v === 'string' && (v.startsWith('=') || v.includes('{{'));

// Пустота обязательных — общий isEmptyRequiredParamValue (graph.ts): пустой массив
// multiOptions и пустой resourceLocator {mode, value:''} — «не заполнено» в ОБОИХ
// линтах сразу (расхождение формы шага и деплой-линта — источник багов активации).

// Союз статических значений options/multiOptions видимых определений параметра.
// null — проверять нельзя: среди видимых есть не-options определение или динамический
// список (loadOptionsMethod / пустые options) — консервативно молчим, не врём ошибкой.
function collectOptionValues(defs: CatalogParam[]): Set<string | number | boolean> | null {
  const values = new Set<string | number | boolean>();
  for (const d of defs) {
    if (d.type !== 'options' && d.type !== 'multiOptions') return null;
    if (d.typeOptions && 'loadOptionsMethod' in d.typeOptions) return null;
    if (!Array.isArray(d.options) || d.options.length === 0) return null;
    for (const o of d.options) {
      const v = (o as { value?: unknown } | null)?.value;
      if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') values.add(v);
    }
  }
  return values.size > 0 ? values : null;
}

// «виден только при resource=message и binaryData=false» — условия первого определения,
// чтобы предупреждение о скрытом параметре было действием, а не констатацией.
function showCondsHint(defs: CatalogParam[]): string {
  const show = defs[0]?.displayOptions?.show;
  if (!show) return '';
  const conds = Object.entries(show)
    .filter(([field, vals]) => !field.startsWith('@') && Array.isArray(vals))
    .map(([field, vals]) => `${field}=${(vals as unknown[]).join('|')}`);
  if (conds.length === 0) return '';
  return ` (виден только при ${conds.join(' и ')}${defs.length > 1 ? ' и т.п.' : ''})`;
}

const MAX_OPTION_HINT = 15; // сколько допустимых значений показывать в ошибке

const optionListHint = (values: Set<string | number | boolean>): string => {
  const list = [...values].map(String);
  return list.length > MAX_OPTION_HINT
    ? `${list.slice(0, MAX_OPTION_HINT).join(', ')}… (всего ${list.length})`
    : list.join(', ');
};

// ── Полная сверка формы сложных параметров (docs/archive/BUILDER-VERIFY-PLAN.md, этап 1) ─────
// Карточек и точечных правил мало: модель выдумывает ФОРМУ вложенных структур — обёртки
// и имена групп fixedCollection, ключи элементов, {mode,value} у resourceLocator,
// {mappingMode,value} у resourceMapper. n8n незнакомое молча игнорирует: схема «зелёная»,
// а не работает. Сверяем значения рекурсивно с полным params_json каталога; в тексте
// ошибки — мини-справка «как надо» из каталога, чтобы бот чинил за один виток (1.2).

/** Группа fixedCollection в params_json: { name, values: INodeProperties[] }. */
type FixedGroupDef = { name?: string; values?: CatalogParam[] };

const fixedGroups = (def: CatalogParam): FixedGroupDef[] =>
  Array.isArray(def.options) ? (def.options as FixedGroupDef[]).filter((g) => isObj(g)) : [];

const collectionProps = (def: CatalogParam): CatalogParam[] =>
  Array.isArray(def.options)
    ? (def.options as CatalogParam[]).filter((p) => isObj(p) && typeof p.name === 'string' && !!p.name)
    : [];

const rlModeNames = (defs: CatalogParam[]): string[] => [
  ...new Set(
    defs
      .flatMap((d) => (Array.isArray(d.modes) ? d.modes : []))
      .map((m) => (m as { name?: unknown } | null)?.name)
      .filter((n): n is string => typeof n === 'string' && n.length > 0),
  ),
];

const MAX_EXAMPLE_LEN = 400;
const MAX_EXAMPLE_FIELDS = 6;
const MAX_SHAPE_DEPTH = 4;

/** Пример значения поля из каталога: default, а без него — скелет по типу. */
function exampleValue(def: CatalogParam, depth = 0): unknown {
  const d = def.default;
  if (
    d !== undefined &&
    !isEmptyValue(d) &&
    !(isObj(d) && Object.keys(d).length === 0) &&
    !(Array.isArray(d) && d.length === 0)
  ) {
    return d;
  }
  if (depth >= 2) return '…';
  switch (def.type) {
    case 'number':
      return 0;
    case 'boolean':
      return false;
    case 'options':
      return (def.options?.[0] as { value?: unknown } | undefined)?.value ?? '…';
    case 'multiOptions':
      return [];
    case 'fixedCollection': {
      const g = fixedGroups(def).find((x) => x.name);
      if (!g?.name) return {};
      const elem = Object.fromEntries(
        (g.values ?? [])
          .slice(0, MAX_EXAMPLE_FIELDS)
          .map((v) => [v.name, exampleValue(v, depth + 1)]),
      );
      return { [g.name]: def.typeOptions?.multipleValues === true ? [elem] : elem };
    }
    case 'collection':
      return Object.fromEntries(
        collectionProps(def)
          .slice(0, MAX_EXAMPLE_FIELDS)
          .map((p) => [p.name, exampleValue(p, depth + 1)]),
      );
    case 'resourceLocator':
      return { __rl: true, mode: rlModeNames([def])[0] ?? 'id', value: '…' };
    case 'resourceMapper':
      return { mappingMode: 'defineBelow', value: { колонка: 'значение' } };
    default:
      return '…';
  }
}

/** Мини-справка «как надо» для текста ошибки (1.2). */
function exampleHint(def: CatalogParam): string {
  const s = JSON.stringify(exampleValue(def));
  return ` Как надо (из каталога): ${s.length > MAX_EXAMPLE_LEN ? `${s.slice(0, MAX_EXAMPLE_LEN)}…` : s}`;
}

/** Контекст корня узла для видимости вложенных полей (Ф13(2)(4)): значения, слитые
 *  с default'ами, + typeVersion — для «/…»- и @version-условий подполей коллекций. */
type RootParamsCtx = { params: Record<string, unknown>; version?: number };

/**
 * Ключи одного элемента против определений полей: незнакомые ключи — ошибка со списком
 * допустимых; знакомые сложные — рекурсия, options — сверка значения. topLevel — имена
 * верхнего уровня узла (для наводки «перенеси туда»; на вложенных уровнях null).
 */
function lintElementKeys(
  label: string,
  stepId: string,
  path: string,
  propDefs: CatalogParam[],
  el: Record<string, unknown>,
  errors: LintIssue[],
  topLevel: ReadonlySet<string> | null,
  root: RootParamsCtx | null,
  depth: number,
): void {
  const byName = new Map<string, CatalogParam[]>();
  const defaults = new Map<string, unknown>();
  for (const p of propDefs) {
    const list = byName.get(p.name);
    if (list) list.push(p);
    else byName.set(p.name, [p]);
    if (!defaults.has(p.name)) defaults.set(p.name, p.default);
  }

  // ATTRIBUTION_KEYS не считаем чужими: stripN8nAttribution при деплое пишет их во ВСЕ
  // top-level коллекции узла безусловно, а C2-синк возвращает такой workflow в канон —
  // ругаться на собственный служебный ключ нельзя (смоук по живым черновикам 2026-07-07).
  const strays = Object.keys(el).filter((k) => !byName.has(k) && !ATTRIBUTION_KEYS.includes(k));
  if (strays.length > 0) {
    const toTop = topLevel ? strays.filter((k) => topLevel.has(k)) : [];
    errors.push({
      stepId,
      message:
        `узел ${label}: в «${path}» нет пол${strays.length > 1 ? 'ей' : 'я'} ` +
        `${strays.map((s) => `«${s}»`).join(', ')} — n8n молча их игнорирует` +
        (toTop.length > 0
          ? `. ${toTop.map((s) => `«${s}»`).join(', ')} — параметр${toTop.length > 1 ? 'ы' : ''} верхнего уровня узла, перенеси туда`
          : `. Допустимые ключи: ${[...byName.keys()].join(', ')}`),
    });
  }

  for (const [k, v] of Object.entries(el)) {
    const kd = byName.get(k);
    if (!kd) continue;
    // Видимость поля внутри элемента считается по соседям этого же элемента; корневые
    // «/…»-пути (к СУЩЕСТВУЮЩИМ полям корня) и @version — по контексту корня узла
    // (root, Ф13(2)(4)), как в автоформе (visibleCollectionSubs). Несчитаемое (внешние
    // имена, «/»-пути к полям, которых у узла нет, прочие @-мета) отбрасывается — иначе
    // прячутся ВСЕ определения и линт судит значения по чужому определению (chatTrigger
    // объявлял responseMode:'streaming' недопустимым — HOW-IT-WORKS §16).
    const visible = localizeDisplayConditions(kd, new Set(byName.keys()), {
      ...(root ? { rootNames: new Set(Object.keys(root.params)) } : {}),
      version: root?.version !== undefined,
    }).filter((d) => paramVisible(d, el, defaults, root?.version, root?.params));
    if (visible.length === 0) continue;
    lintParamShape(label, stepId, `${path}.${k}`, visible, v, errors, null, root, depth + 1);
    const allowed = collectOptionValues(visible);
    if (!allowed) continue;
    const candidates = (Array.isArray(v) ? v : [v]).filter(
      (x): x is string | number | boolean =>
        (typeof x === 'string' || typeof x === 'number' || typeof x === 'boolean') && !isExpression(x),
    );
    for (const c of candidates) {
      if (!allowed.has(c)) {
        errors.push({
          stepId,
          message:
            `узел ${label}: значение «${String(c)}» недопустимо для «${path}.${k}». ` +
            `Допустимые: ${optionListHint(allowed)}`,
        });
      }
    }
  }
}

/**
 * Сверка формы одного значения с каталогом (fixedCollection/collection/resourceLocator/
 * resourceMapper). Простые типы и выражения не трогает; смешанные/пустые определения —
 * консервативно молчит (лучше пропустить, чем врать ошибкой).
 */
function lintParamShape(
  label: string,
  stepId: string,
  path: string,
  defs: CatalogParam[],
  value: unknown,
  errors: LintIssue[],
  topLevel: ReadonlySet<string> | null,
  root: RootParamsCtx | null,
  depth = 0,
): void {
  if (depth > MAX_SHAPE_DEPTH || defs.length === 0) return;
  if (value === undefined || value === null || isExpression(value)) return;
  const type = defs[0]!.type;
  if (!type || defs.some((d) => d.type !== type)) return;

  if (type === 'fixedCollection') {
    const groups = new Map<string, FixedGroupDef[]>();
    for (const g of defs.flatMap(fixedGroups)) {
      if (!g.name) continue;
      const list = groups.get(g.name);
      if (list) list.push(g);
      else groups.set(g.name, [g]);
    }
    if (Array.isArray(value)) {
      errors.push({
        stepId,
        message:
          `узел ${label}: «${path}» — массив, а n8n ждёт объект-обёртку ` +
          `{${[...groups.keys()].join('|') || 'имяГруппы'}: [...]} — без неё значения молча пропадут.` +
          exampleHint(defs[0]!),
      });
      return;
    }
    if (!isObj(value)) {
      errors.push({
        stepId,
        message: `узел ${label}: «${path}» должен быть объектом {группа: элементы}.${exampleHint(defs[0]!)}`,
      });
      return;
    }
    if (groups.size === 0) return; // групп в каталоге нет — сверять не с чем
    for (const [gname, gval] of Object.entries(value)) {
      const gdefs = groups.get(gname);
      if (!gdefs) {
        errors.push({
          stepId,
          message:
            `узел ${label}: в «${path}» нет группы «${gname}» — n8n молча её игнорирует. ` +
            `Допустимые группы: ${[...groups.keys()].join(', ')}.${exampleHint(defs[0]!)}`,
        });
        continue;
      }
      const valueDefs = gdefs.flatMap((g) => g.values ?? []).filter((v) => typeof v.name === 'string' && v.name);
      if (valueDefs.length === 0) continue;
      for (const el of Array.isArray(gval) ? gval : [gval]) {
        if (!isObj(el)) continue; // не объект — выражение/мусор, форму не сверить
        lintElementKeys(label, stepId, `${path}.${gname}`, valueDefs, el, errors, null, root, depth);
      }
    }
    return;
  }

  if (type === 'collection') {
    const props = defs.flatMap(collectionProps);
    if (props.length === 0) return; // динамическая коллекция — состав неизвестен
    for (const el of Array.isArray(value) ? value : [value]) {
      if (!isObj(el)) continue;
      lintElementKeys(label, stepId, path, props, el, errors, topLevel, root, depth);
    }
    return;
  }

  if (type === 'resourceLocator') {
    const modes = rlModeNames(defs);
    if (!isObj(value)) {
      errors.push({
        stepId,
        message:
          `узел ${label}: «${path}» — n8n ждёт объект {mode, value}` +
          `${modes.length > 0 ? ` (mode: ${modes.join(' | ')})` : ''}, а не голое значение.` +
          exampleHint(defs[0]!),
      });
      return;
    }
    const mode = value.mode;
    if (typeof mode !== 'string' || !mode.trim()) {
      errors.push({
        stepId,
        message: `узел ${label}: в «${path}» не задан mode — как адресовать значение.${exampleHint(defs[0]!)}`,
      });
    } else if (!isExpression(mode) && modes.length > 0 && !modes.includes(mode)) {
      errors.push({
        stepId,
        message: `узел ${label}: mode «${mode}» недопустим для «${path}». Допустимые: ${modes.join(', ')}`,
      });
    }
    if (!('value' in value)) {
      errors.push({
        stepId,
        message: `узел ${label}: в «${path}» нет value — само значение потерялось.${exampleHint(defs[0]!)}`,
      });
    }
    return;
  }

  if (type === 'resourceMapper') {
    if (!isObj(value)) {
      errors.push({
        stepId,
        message: `узел ${label}: «${path}» — n8n ждёт объект {mappingMode, value}.${exampleHint(defs[0]!)}`,
      });
      return;
    }
    const mm = value.mappingMode;
    if (typeof mm !== 'string' || !mm.trim()) {
      errors.push({
        stepId,
        message:
          `узел ${label}: в «${path}» не задан mappingMode — n8n не поймёт, откуда брать поля ` +
          `(обычно "defineBelow", сами поля — в value).${exampleHint(defs[0]!)}`,
      });
      return;
    }
    if (mm === 'defineBelow' && !isExpression(value.value) && !isObj(value.value)) {
      errors.push({
        stepId,
        message:
          `узел ${label}: в «${path}» при mappingMode="defineBelow" value должен быть объектом ` +
          `{колонка: значение}.${exampleHint(defs[0]!)}`,
      });
    }
  }
}

// params.model узлов Scriptera AI — по живому списку шлюза (перенос из apps/mcp
// model-caps: та же логика, но ходит по узлам workflow, а не по шагам ленты).
function lintScripteraModels(wf: WorkflowJson, models: ScripteraModelInfo[]): LintIssue[] {
  const errors: LintIssue[] = [];
  const byName = new Map(models.map((m) => [m.name, m]));
  const editList = models.filter((m) => m.caps?.edit).map((m) => m.name);
  const kindList = (kind: string) => models.filter((m) => m.kind === kind).map((m) => m.name);

  for (const node of wf.nodes) {
    if (node.disabled === true || node.type !== 'CUSTOM.scripteraAi') continue;
    const params = node.parameters ?? {};
    const model = params.model;
    const resource = typeof params.resource === 'string' ? params.resource : 'text';
    const operation = typeof params.operation === 'string' ? params.operation : '';
    if (typeof model !== 'string' || model.trim() === '') {
      errors.push({
        stepId: node.id,
        message: `params.model обязателен — id модели строкой (доступные: ${models.map((m) => m.name).join(', ')})`,
      });
      continue;
    }
    if (isExpression(model)) continue;
    const info = byName.get(model);
    if (!info) {
      const hint = resource === 'image' && operation === 'edit' ? editList : kindList(resource);
      errors.push({
        stepId: node.id,
        message:
          `модели «${model}» не существует или она не открыта пользователю. ` +
          `Доступные для этого случая: ${hint.length ? hint.join(', ') : 'НЕТ НИ ОДНОЙ — скажи пользователю честно'}`,
      });
      continue;
    }
    if (info.kind && info.kind !== resource) {
      errors.push({
        stepId: node.id,
        message: `модель «${model}» — это ${info.kind}, а resource=${resource}. Доступные: ${kindList(resource).join(', ') || 'нет'}`,
      });
      continue;
    }
    if (resource === 'image' && operation === 'edit' && info.caps?.edit !== true) {
      errors.push({
        stepId: node.id,
        message: `модель «${model}» не умеет edit (переделку фото). Edit-модели: ${editList.join(', ') || 'нет'}`,
      });
    }
    if (resource === 'image' && operation === 'generate' && info.caps && info.caps.minInputImages > 0) {
      errors.push({
        stepId: node.id,
        message: `модель «${model}» работает только с картинкой на входе (operation=edit), для generate не годится`,
      });
    }
  }
  return errors;
}

// Связность: висячие connections — ошибка, недостижимые от триггера узлы — предупреждение.
// Достижимость считаем НЕнаправленно по всем портам (main, error, ai_*): sub-узлы
// (ai_tool/ai_languageModel) соединены В агент — направленный обход от триггера их не видит.
function lintConnectivity(
  wf: WorkflowJson,
  catalog: Map<string, CatalogNodeInfo>,
  errors: LintIssue[],
  warnings: LintIssue[],
): void {
  const nodeNames = new Set(wf.nodes.map((n) => n.name));
  const adj = new Map<string, Set<string>>();
  const link = (a: string, b: string): void => {
    (adj.get(a) ?? adj.set(a, new Set()).get(a)!).add(b);
    (adj.get(b) ?? adj.set(b, new Set()).get(b)!).add(a);
  };
  for (const [from, byPort] of Object.entries(wf.connections)) {
    if (!nodeNames.has(from)) {
      errors.push({ stepId: '', message: `connections: узла «${from}» нет в схеме — убери запись или добавь узел` });
      continue;
    }
    for (const outputs of Object.values(byPort)) {
      for (const targets of outputs) {
        for (const t of targets ?? []) {
          if (!nodeNames.has(t.node)) {
            errors.push({
              stepId: '',
              message: `connections: узел «${from}» ссылается на несуществующий «${t.node}» — висячая связь`,
            });
          } else {
            link(from, t.node);
          }
        }
      }
    }
  }

  const triggers = wf.nodes.filter((n) => n.disabled !== true && catalog.get(n.type)?.isTrigger);
  if (triggers.length === 0 || wf.nodes.length < 2) return; // «нет триггера» уже предупредили
  const reached = new Set<string>(triggers.map((n) => n.name));
  const queue = [...reached];
  while (queue.length > 0) {
    for (const next of adj.get(queue.pop()!) ?? []) {
      if (!reached.has(next)) {
        reached.add(next);
        queue.push(next);
      }
    }
  }
  for (const n of wf.nodes) {
    // Заметка-стикер — не шаг: соединять её не с чем и незачем (она объясняет схему
    // человеку). Раньше на каждую заметку шаблона капало предупреждение «не соединён с
    // триггером» — мусор в списке, из-за которого не видно настоящих (К15).
    if (canonicalNodeType(n.type) === 'n8n-nodes-base.stickyNote') continue;
    if (n.disabled !== true && !reached.has(n.name)) {
      warnings.push({
        stepId: n.id,
        message: `узел «${n.name}» не соединён с триггером — при работе сценария он не выполнится`,
      });
    }
  }
}

// Ф15 аудита: довесок (ports.attach из каталога — модель/память/инструмент ai_*-гнёзд)
// шагом главного потока не бывает: main-связи с ним n8n игнорирует, узел молча не
// работает (класс бага владельца «довесок в линии»). Ловим оба края: довесок с
// main-связью и довесок-сирота без единого ai_*-ребра к шагу-хозяину.
// Ф16+Ф17: довесочность считается ПО РЕЖИМУ (nodePortsFor) — векторное хранилище
// в retrieve/retrieve-as-tool довесок, в insert/load обычный шаг; заодно ловим
// довесок, пристёгнутый НЕ в своё гнездо (retrieve-as-tool на ребре ai_vectorStore).
function lintAiAttachments(
  wf: WorkflowJson,
  catalog: Map<string, CatalogNodeInfo>,
  errors: LintIssue[],
): void {
  const mainLinked = new Set<string>(); // имена узлов с main-связью в любую сторону
  const aiPortsOut = new Map<string, Set<string>>(); // имя → ai_*-порты его исходящих рёбер
  for (const [from, byPort] of Object.entries(wf.connections)) {
    for (const [port, outputs] of Object.entries(byPort)) {
      for (const targets of outputs) {
        for (const t of targets ?? []) {
          if (port === 'main') {
            mainLinked.add(from);
            mainLinked.add(t.node);
          } else if (port.startsWith('ai_')) {
            const set = aiPortsOut.get(from) ?? new Set<string>();
            set.add(port);
            aiPortsOut.set(from, set);
          }
        }
      }
    }
  }
  for (const n of wf.nodes) {
    if (n.disabled === true) continue;
    const info = catalog.get(n.type);
    if (!info) continue;
    const attach = nodePortsFor(info, n.parameters ?? {}, n.typeVersion).attach;
    if (!attach) continue;
    const outPorts = aiPortsOut.get(n.name);
    if (mainLinked.has(n.name)) {
      errors.push({
        stepId: n.id,
        message:
          `«${n.name}» — служебный довесок (модель/память/инструмент): шагом в цепочку он не встаёт, ` +
          'main-связи с ним не работают. Пристегни его к ai_*-гнезду подходящего шага, main-связи убери',
      });
    } else if (!outPorts || outPorts.size === 0) {
      errors.push({
        stepId: n.id,
        message:
          `«${n.name}» — довесок не пристёгнут ни к одному шагу (нет ai_*-связи): ` +
          'пристегни его к гнезду подходящего шага или удали',
      });
    } else if (![...outPorts].every((p) => p === attach)) {
      errors.push({
        stepId: n.id,
        message:
          `«${n.name}» пристёгнут не в своё гнездо: в текущем режиме он встаёт в «${attach}», ` +
          `а связан через «${[...outPorts].filter((p) => p !== attach).join('», «')}». ` +
          'Смени режим узла или перецепи его в подходящее гнездо',
      });
    }
  }
}

// ── BUILDER-UX-FIXLIST 4.2: одна база — одна модель отпечатков ───────────────────────
// Векторная база «помнит» модель только фактом записи: тексты, положенные одной
// embedding-моделью, другая модель при поиске МОЛЧА не находит (ошибки нет — пустая
// или чужая выдача). Ловим статически: у всех узлов Qdrant с одной и той же базой
// модель пристёгнутого узла отпечатков обязана совпадать — и внутри сценария
// (вставка + поиск), и между сценариями клиента (наполнение и бот у владельца живут
// в РАЗНЫХ сценариях — opts.embeddings собирают web/mcp по остальным черновикам).

const EMBEDDINGS_TYPE_PREFIX = '@n8n/n8n-nodes-langchain.embeddings';

/** Использование базы отпечатков одним узлом Qdrant: какая база, какой моделью. */
export interface EmbeddingUsage {
  collection: string;
  /** Модель пристёгнутого узла отпечатков; null — статически не видна
   *  (довесок не пристёгнут или модель выражением). */
  model: string | null;
  nodeId: string;
  nodeName: string;
}

/** Та же база в ДРУГОМ сценарии клиента — для кросс-сверки при линте. */
export interface KnownEmbeddingUsage {
  collection: string;
  model: string;
  /** Название сценария — ошибка называет, где живёт вторая сторона базы. */
  scenario: string;
}

// Статическое имя базы: строка руками (mode id) или из списка (mode list);
// выражение оценит рантайм — молчим, пустоту ловит required-проверка.
function staticCollectionName(v: unknown): string | null {
  const raw = isObj(v) ? v.value : v;
  if (typeof raw !== 'string') return null;
  const name = raw.trim();
  return name !== '' && !isExpression(name) ? name : null;
}

// Модель узла отпечатков: явный параметр или default каталога — нетронутый селект
// означает модель по умолчанию n8n, для сверки она такая же настоящая, как выбранная.
function embeddingModelOf(
  emb: WorkflowNode | undefined,
  catalog: Map<string, CatalogNodeInfo>,
): string | null {
  if (!emb) return null;
  const params = emb.parameters ?? {};
  const raw = params.model;
  const value = isObj(raw) ? raw.value : raw; // на случай resourceLocator-формы
  if (typeof value === 'string' && value.trim() !== '') {
    return isExpression(value) ? null : value.trim();
  }
  const info = catalog.get(emb.type);
  if (!info) return null;
  const def = resolveParamDefaults(info.params, params, emb.typeVersion).get('model');
  return typeof def === 'string' && def !== '' ? def : null;
}

/** Все узлы Qdrant воркфлоу со статическим именем базы и моделью их отпечатков.
 *  Экспорт: web/mcp собирают этим карту «база → модель» по остальным сценариям
 *  клиента (opts.embeddings), раздел «Знания» показывает модель каждой базы. */
export function collectEmbeddingUsage(
  wf: WorkflowJson,
  catalog: Map<string, CatalogNodeInfo>,
): EmbeddingUsage[] {
  const byName = new Map(wf.nodes.map((n) => [n.name, n]));
  // имя узла Qdrant → его довесок отпечатков (ребро ai_embedding идёт ОТ довеска к цели)
  const embFor = new Map<string, WorkflowNode>();
  for (const [from, byPort] of Object.entries(wf.connections)) {
    const emb = byName.get(from);
    if (!emb || emb.disabled === true || !emb.type.startsWith(EMBEDDINGS_TYPE_PREFIX)) continue;
    for (const targets of byPort['ai_embedding'] ?? []) {
      for (const t of targets ?? []) embFor.set(t.node, emb);
    }
  }
  const usage: EmbeddingUsage[] = [];
  for (const n of wf.nodes) {
    if (n.disabled === true || n.type !== QDRANT_VECTOR_STORE_TYPE) continue;
    const collection = staticCollectionName((n.parameters ?? {}).qdrantCollection);
    if (!collection) continue;
    usage.push({
      collection,
      model: embeddingModelOf(embFor.get(n.name), catalog),
      nodeId: n.id,
      nodeName: n.name,
    });
  }
  return usage;
}

function lintEmbeddingModels(
  wf: WorkflowJson,
  catalog: Map<string, CatalogNodeInfo>,
  known: KnownEmbeddingUsage[] | null | undefined,
  errors: LintIssue[],
): void {
  const usage = collectEmbeddingUsage(wf, catalog);
  if (usage.length === 0) return;

  // Внутри сценария: две стороны одной базы с разными моделями → ошибка на каждом узле.
  const byCollection = new Map<string, EmbeddingUsage[]>();
  for (const u of usage) {
    (byCollection.get(u.collection) ?? byCollection.set(u.collection, []).get(u.collection)!).push(u);
  }
  for (const [collection, list] of byCollection) {
    const withModel = list.filter((u): u is EmbeddingUsage & { model: string } => u.model !== null);
    if (new Set(withModel.map((u) => u.model)).size < 2) continue;
    const sides = withModel.map((u) => `«${u.nodeName}» — «${u.model}»`).join(', ');
    for (const u of withModel) {
      errors.push({
        stepId: u.nodeId,
        message:
          `база «${collection}»: у её шагов разные модели отпечатков (${sides}). ` +
          'Записанное одной моделью другая при поиске молча не находит — у одной базы ' +
          'модель одна. Поставь одну и ту же модель в узлах отпечатков обоих шагов; ' +
          'если модель сменилась — очисти базу и наполни заново',
      });
    }
  }

  // Между сценариями: вторая сторона базы живёт в другом сценарии клиента.
  if (!known || known.length === 0) return;
  for (const u of usage) {
    if (u.model === null) continue;
    const clash = known.filter((k) => k.collection === u.collection && k.model !== u.model);
    if (clash.length === 0) continue;
    const others = [...new Set(clash.map((k) => `«${k.scenario}» — модель «${k.model}»`))].join(', ');
    errors.push({
      stepId: u.nodeId,
      message:
        `база «${u.collection}»: здесь модель отпечатков «${u.model}», а с той же базой работает ` +
        `сценарий ${others}. Записанное одной моделью другая при поиске молча не находит — ` +
        'поставь одну модель в обоих сценариях; если модель сменилась — очисти базу и наполни заново',
    });
  }
}

// ── Планка качества чат-ботов (этап A4 — docs/archive/BUILDER-QUALITY-SYNC-PLAN.md) ─────────
// Стандарт «адекватного бота» (заказ владельца, приёмка 2026-07-05: /start → тишина,
// caption без fallback → 400 шлюза, падение шага молчит). Предупреждения, НЕ ошибки:
// схема исполнима, но бот будет молчать — тексты действием, что именно дописать.
// Канон обработки ошибок — error-выход узла (onError: "continueErrorOutput";
// n8n 1.70 исполняет его в ядре — NodeHelpers.js:1003, проверено на живом инстансе).
// errorWorkflow НЕ канон: это ВТОРОЙ workflow, а сценарий кабинета — всегда один.

export const TELEGRAM_TRIGGER_TYPE = 'n8n-nodes-base.telegramTrigger';
// BUILDER-UX-FIXLIST 2.6: имя новой базы Qdrant вписывают руками (вкладка «ID») —
// формат держим статически, до рантайма: латиница/цифры/-/_, до 48 знаков.
// Экспорт: web/mcp по этому типу решают, нужна ли кросс-сверка отпечатков (4.2).
export const QDRANT_VECTOR_STORE_TYPE = '@n8n/n8n-nodes-langchain.vectorStoreQdrant';
const QDRANT_COLLECTION_NAME_RE = /^[A-Za-z0-9_-]{1,48}$/;
const BRANCHING_TYPES = new Set([
  'n8n-nodes-base.if',
  'n8n-nodes-base.switch',
  'n8n-nodes-base.filter',
]);
// Движковые узлы Scriptera (медиа/файлы через шлюз-раннер, ENGINES-AND-INPUTS-PLAN):
// внешний вызов, который может упасть; сюда же ждут файл на входе (детект expectsMedia).
// Экспорт: тестовый прогон (test-run.ts) глушит их pinData-заглушками — один список.
export const ENGINE_NODE_TYPES = new Set([
  'CUSTOM.scripteraVideo',
  'CUSTOM.scripteraAudio',
  'CUSTOM.scripteraImages',
  'CUSTOM.scripteraArchive',
  'CUSTOM.scripteraOcr',
  'CUSTOM.scripteraDocuments',
  'CUSTOM.scripteraConvert',
]);
// Шаги, которые ходят во внешние сервисы и потому падают (ИИ, движки Scriptera, HTTP):
// без error-выхода юзер бота получает тишину. Узлы отправки Telegram сюда не входят — их
// error-выход пришлось бы вести в такую же отправку (рекурсия обработки).
const FALLIBLE_TYPES = new Set(['CUSTOM.scripteraAi', ...ENGINE_NODE_TYPES, 'n8n-nodes-base.httpRequest']);

/** Все строковые значения параметров узла, с любой глубины вложенности. */
export function collectParamStrings(value: unknown, out: string[]): void {
  if (typeof value === 'string') out.push(value);
  else if (Array.isArray(value)) for (const v of value) collectParamStrings(v, out);
  else if (isObj(value)) for (const v of Object.values(value)) collectParamStrings(v, out);
}

// Два Telegram-триггера в одном workflow (проба владельца 2026-07-07: внешний ассистент
// разнёс message и callback_query по двум триггерам). У бота ОДИН webhook — n8n не даст
// двум триггерам слушать одного бота, сценарий не заработает. Канон: один триггер со
// всеми updates + Switch/If по типу события (TELEGRAM_BOT_STANDARD, карточка триггера).
function lintSingleTelegramTrigger(wf: WorkflowJson, errors: LintIssue[]): void {
  const triggers = wf.nodes.filter((n) => n.disabled !== true && n.type === TELEGRAM_TRIGGER_TYPE);
  for (const extra of triggers.slice(1)) {
    errors.push({
      stepId: extra.id,
      message:
        `узел «${extra.name}»: в сценарии может быть только ОДИН Telegram-триггер — у бота один ` +
        'канал событий, второй триггер работать не будет. Перечисли все события в updates одного ' +
        'триггера (например ["message","callback_query"]) и разведи их после него узлом Switch/If. ' +
        'Эталон — в карточке узла Telegram Trigger (get_node)',
    });
  }
}

/**
 * АККАУНТ ШАГА ЗАПУСКА Telegram/Instagram ОБЯЗАТЕЛЕН (Ф0, BOT-INTEGRATIONS, владелец
 * 02.08). Состояние считает ОБЩЕЕ ядро botAccountState — то же, что показывает выбор в
 * «Начале», отбивает включение и привязывает доступ при установке (инвариант К34).
 *
 * Почему ОШИБКА: не выбранный из нескольких аккаунт раньше уезжал на случайный бот, а
 * удалённый — молча подменялся другим, и сценарий начинал отвечать чужим лицом. Схема с
 * такой дырой неисправна до перевыбора. Сверяем только когда список доступов передан
 * (кабинет); 'none' (аккаунтов канала нет вовсе) — штатный «нужен доступ» (missing_creds),
 * второй ошибкой его не дублируем.
 */
function lintBotAccount(
  wf: WorkflowJson,
  creds: readonly InstanceCredRef[],
  errors: LintIssue[],
): void {
  const account = botAccountState(wf, creds);
  if (!account) return;
  if (account.status === 'pick') {
    errors.push({
      stepId: account.stepId,
      message:
        `шаг «${account.stepName}»: не выбран аккаунт, от имени которого работает сценарий, — ` +
        'подключено несколько, выбери нужный в настройках шага запуска',
    });
  } else if (account.status === 'gone') {
    errors.push({
      stepId: account.stepId,
      message:
        `шаг «${account.stepName}»: выбранный аккаунт удалён из подключений — сценарий работать ` +
        'не будет, выбери аккаунт заново в настройках шага запуска',
    });
  }
}

/**
 * ШАГ «ПРОВЕРКА ПОДПИСКИ» С НЕГОДНЫМ КАНАЛОМ (Б3, заказ владельца 31.07).
 *
 * Почему это ОШИБКА, а не предупреждение: без прав администратора Telegram отвечает на
 * getChatMember отказом, узел «Если» получает не тот статус — и человек, который подписан,
 * молча уходит в ветку «не подписан». Снаружи это выглядит как исправный сценарий. Ведём
 * себя как с «подключите кассу»: не включаем, пока канал не подтверждён.
 *
 * Сверяем ТОЛЬКО когда список каналов бота передан (кабинет при включении). Выражение
 * в поле канала не трогаем: его значение известно лишь в бою.
 */
export function lintSubscribeChannels(
  wf: WorkflowJson,
  channels: readonly LintTgChannel[] | null | undefined,
  errors: LintIssue[],
): void {
  if (!channels) return;
  const okAddr = new Set(
    channels.filter((c) => c.canCheck).map((c) => c.address.replace(/^@/, '').toLowerCase()),
  );
  const known = new Set(channels.map((c) => c.address.replace(/^@/, '').toLowerCase()));
  for (const n of wf.nodes) {
    if (n.disabled === true || canonicalNodeType(n.type) !== 'n8n-nodes-base.telegram') continue;
    const p = n.parameters ?? {};
    if (p.resource !== 'chat' || p.operation !== 'member') continue;
    const raw = typeof p.chatId === 'string' ? p.chatId.trim() : '';
    if (!raw) {
      errors.push({
        stepId: n.id,
        message: `шаг «${n.name}»: не выбран канал для проверки подписки — выберите его в настройках шага`,
      });
      continue;
    }
    if (isExpression(raw)) continue;
    const key = raw.replace(/^@/, '').toLowerCase();
    if (okAddr.has(key)) continue;
    errors.push({
      stepId: n.id,
      message: known.has(key)
        ? `шаг «${n.name}»: бот не администратор канала ${raw} — проверить подписку он не сможет. ` +
          'Добавьте бота в канал администратором и нажмите «Проверить» в настройках шага'
        : `шаг «${n.name}»: канал ${raw} не подтверждён — добавьте бота в него администратором ` +
          'и нажмите «Проверить» в настройках шага',
    });
  }
}

/**
 * Шаг отправки ВЛОЖЕНИЯ, у которого файл не выбран (В5-ПРИЁМКА К1). В каталоге узла
 * параметр `file` необязательный, так что штатная проверка обязательных полей молчит, а
 * Телеграм на пустой файл отвечает отказом уже в бою — человек видит тишину вместо
 * картинки. Отправку бинарём из прошлого шага (`binaryData: true`) не трогаем: там файла
 * в параметрах и не должно быть.
 */
const TG_ATTACH_SEND_OPS = new Set(['sendPhoto', 'sendVideo', 'sendAudio', 'sendDocument', 'sendAnimation', 'sendSticker']);

function lintChatAttachments(wf: WorkflowJson, warnings: LintIssue[]): void {
  for (const n of wf.nodes) {
    if (n.disabled === true || canonicalNodeType(n.type) !== 'n8n-nodes-base.telegram') continue;
    const p = n.parameters ?? {};
    if (typeof p.operation !== 'string' || !TG_ATTACH_SEND_OPS.has(p.operation)) continue;
    if (p.binaryData === true) continue;
    if (typeof p.file === 'string' && p.file.trim() !== '') continue;
    warnings.push({
      stepId: n.id,
      message:
        `узел «${n.name}»: файл вложения не выбран — шаг отправит пустоту, и пользователь ничего не ` +
        'получит. Выбери файл на карточке шага',
    });
  }
}

/**
 * К0 (MINIMAL-PRODUCT В5) — КАНАЛЬНЫЙ ЛИНТ. Сценарий разговаривает в ОДНОМ канале; всё, что
 * этому противоречит, не сработает. Предупреждениями, не ошибками: старые схемы обязаны
 * открываться и правиться, замок на НОВЫЕ поломки стоит на записи схемы.
 *
 * Сами замечания считает channels.ts (channelNotes) — ОДИН источник и для этих строк, и для
 * плашек кабинета: расхождений «бот говорит одно, карточка другое» не бывает. Здесь только
 * русский текст для бота, edge-инструментов и scenario_lint; кабинет собирает свой по локали.
 */
function lintChannels(wf: WorkflowJson, warnings: LintIssue[]): void {
  for (const n of channelNotes(wf)) {
    const message =
      n.kind === 'foreignStep'
        ? `шаг «${n.nodeName}» из другого канала (${n.stepChannel}), а сценарий разговаривает ` +
          `в канале ${n.channel} — отвечать этому шагу некому, он не сработает. Замени его ` +
          `шагом канала ${n.channel} или убери`
        : n.kind === 'crossChannel'
          ? `в шаг «${n.nodeName}» ведёт связь из канала ${n.stepChannel} — разговор из одного ` +
            'канала в другой не продолжается'
          : n.kind === 'startConflict'
            ? `в схеме шаги запуска разных каналов — сценарий может разговаривать только ` +
              'в одном. Оставь один запуск'
            : n.kind === 'noReplyAddress'
              ? `шаг «${n.nodeName}» отвечает в канал ${n.channel}, но шага запуска разговора ` +
                'в схеме нет — адрес собеседника взять неоткуда, и сообщение уйдёт в пустоту'
              : n.kind === 'replyWindow'
                ? `до шага «${n.nodeName}» ${
                    Number.isFinite(n.waitHours ?? 0)
                      ? `набегает пауз на ${Math.round(n.waitHours ?? 0)} ч`
                      : 'стоит ожидание неизвестной длины'
                  }` +
                  `${n.waitName ? ` (самая долгая пауза — «${n.waitName}»)` : ''}, а ответить в канале ` +
                  `${n.channel} можно только в течение ${n.hours} часов после сообщения ` +
                  'пользователя — позже сообщение не уйдёт. Сократи паузы или отвечай раньше'
                : n.kind === 'startNoActivity'
                  ? // Два случая одной фразой: запуск не от человека (рассылка, расписание)
                    // и запуск от человека, который окна не открывает (комментарий,
                    // эфир) — CHAT-LOOK-PLAN 14.3. Врать «запускается не сообщением
                    // человека» про комментарий нельзя: комментарий он и написал.
                    `шаг «${n.nodeName}» пишет пользователю, а канал ${n.channel} пускает сообщение ` +
                    `только в течение ${n.hours} часов после того, как пользователь написал или ` +
                    'нажал кнопку; запуск этого сценария такого окна не открывает — сообщение ' +
                    'может не дойти'
                  : `у шага «${n.nodeName}» ${n.count} кнопок, а канал ${n.channel} показывает ` +
                    `не больше ${n.max} — лишних пользователь не увидит`;
    warnings.push({ stepId: n.nodeId, message });
  }
}

function lintBotQuality(wf: WorkflowJson, warnings: LintIssue[]): void {
  const active = wf.nodes.filter((n) => n.disabled !== true);
  const trigger = active.find((n) => n.type === TELEGRAM_TRIGGER_TYPE);
  if (!trigger) return; // стандарт — для чат-ботов; расписания/вебхуки не трогаем

  const stringsByNode = new Map<string, string[]>();
  for (const n of active) {
    const acc: string[] = [];
    collectParamStrings(n.parameters ?? {}, acc);
    stringsByNode.set(n.name, acc);
  }

  // (а) бот ждёт медиа (фото/видео/аудио/документ), а ветки на «не то прислали» нет:
  // на текст и /start — тишина. Движковые узлы (аудио/видео/…) и обращения к message.*-медиа
  // считаем ожиданием файла (ENGINES-AND-INPUTS-PLAN: входы стали разнотипными).
  const MEDIA_MESSAGE_FIELDS = ['message.photo', 'message.video', 'message.audio', 'message.voice', 'message.document'];
  const expectsMedia = active.some((n) => {
    const p = n.parameters ?? {};
    if (n.type === 'CUSTOM.scripteraAi' && p.resource === 'image' && p.operation === 'edit') return true;
    if (ENGINE_NODE_TYPES.has(n.type)) return true;
    return (stringsByNode.get(n.name) ?? []).some(
      (s) => isExpression(s) && (s.includes('$binary') || MEDIA_MESSAGE_FIELDS.some((f) => s.includes(f))),
    );
  });
  if (expectsMedia && !active.some((n) => BRANCHING_TYPES.has(n.type))) {
    warnings.push({
      stepId: trigger.id,
      message:
        'бот ждёт файл (фото/видео/аудио/документ), но ветки на остальные сообщения нет — на текст ' +
        'или /start он промолчит. Добавь после триггера If (проверь нужный тип, напр. ' +
        '«{{ $json.message.video }} exists») и в ветке «не то» отправь подсказку, что прислать. ' +
        'Эталон — в карточке узла Telegram Trigger (get_node)',
    });
  }

  // (б) caption без запасного значения: фото без подписи → пустое выражение → шаг падает
  // (приёмка: prompt из caption без fallback давал 400 шлюза).
  for (const n of active) {
    const bad = (stringsByNode.get(n.name) ?? []).some(
      (s) => isExpression(s) && s.includes('.caption') && !s.includes('||') && !s.includes('??'),
    );
    if (bad) {
      warnings.push({
        stepId: n.id,
        message:
          `узел «${n.name}»: подписи к фото может не быть — выражение с caption без запасного ` +
          `значения даст пустоту, и шаг упадёт. Напиши {{ $json.message.caption || 'текст по умолчанию' }}`,
      });
    }
  }

  // (в) падающий шаг без error-выхода: юзер бота получит тишину вместо ответа.
  // «Продолжить при ошибке» (continueRegularOutput, Ф38) тишину тоже снимает — не пилим.
  for (const n of active) {
    if (
      !FALLIBLE_TYPES.has(n.type) ||
      n.onError === 'continueErrorOutput' ||
      n.onError === 'continueRegularOutput'
    )
      continue;
    warnings.push({
      stepId: n.id,
      message:
        `шаг «${n.name}» ходит во внешний сервис и может упасть — тогда бот промолчит. ` +
        'Поставь у узла onError: "continueErrorOutput" и приведи его error-выход (последний ' +
        'выход main) в отправку сообщения «не получилось, попробуйте ещё раз»',
    });
  }
}

// ── Планка качества веб-форм (HOW-IT-WORKS §15) ────────────────────────────────────
// Экраны проверяются ТЕМ ЖЕ валидатором, что исполняют узлы (screenFromNodeParams,
// lib/screen-format пакета узлов — единственный источник истины): брак экрана на
// проде = 500 браузеру, линт ловит его раньше. Выражения {{ … }} n8n подставит при
// показе — на время проверки маскируем их валидными заглушками ПО МЕСТУ (url —
// ссылкой, тексты — строкой); имена в данных (id кнопок, key полей, value вариантов)
// обязаны быть статичными — выражение там остаётся как есть и честно падает
// проверкой формата.
const SCREEN_URL_PARAM_KEYS = new Set(['url']);
const SCREEN_KEY_PARAM_KEYS = new Set(['id', 'key', 'file', 'value']);

// export — для юнитов маскирования (без прогона всего линта).
export function maskScreenExpressions(value: unknown, keyName?: string): unknown {
  if (typeof value === 'string') {
    if (!isExpression(value)) return value;
    if (keyName && SCREEN_KEY_PARAM_KEYS.has(keyName)) return value;
    return keyName && SCREEN_URL_PARAM_KEYS.has(keyName) ? 'https://scriptera.ai/expression' : 'выражение';
  }
  if (Array.isArray(value)) return value.map((v) => maskScreenExpressions(v, keyName));
  if (isObj(value)) {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value)) out[k] = maskScreenExpressions(v, k);
    return out;
  }
  return value;
}

// _warnings: сигнатура едина с остальными линт-функциями; с 1.2а все находки формы — ошибки.
export function lintFormApp(wf: WorkflowJson, errors: LintIssue[], _warnings: LintIssue[]): void {
  const active = wf.nodes.filter((n) => n.disabled !== true);
  const forms = active.filter((n) => n.type === FORM_TRIGGER_TYPE);
  const screens = active.filter((n) => n.type === SCREEN_NODE_TYPE);
  if (forms.length === 0 && screens.length === 0) return;

  // Один триггер «Форма» на сценарий: одна ссылка, один первый экран.
  for (const extra of forms.slice(1)) {
    errors.push({
      stepId: extra.id,
      message:
        `узел «${extra.name}»: триггер «Форма» может быть только ОДИН на сценарий — у формы одна ` +
        'ссылка и один первый экран. Экраны по ходу прохождения — узел «Экран» (CUSTOM.scripteraScreen)',
    });
  }
  if (forms.length === 0) {
    errors.push({
      stepId: screens[0]!.id,
      message:
        '«Экран» работает только в сценарии с триггером «Форма» (CUSTOM.scripteraForm) — пользователю ' +
        'негде открыть форму. Добавь триггер «Форма» с первым экраном',
    });
  }

  const nextButtonsByName = new Map<string, number>();
  for (const node of [...forms, ...screens]) {
    const isFormTrigger = node.type === FORM_TRIGGER_TYPE;
    const params = node.parameters ?? {};
    if (isFormTrigger) {
      const strings: string[] = [];
      collectParamStrings(params, strings);
      if (strings.some(isExpression)) {
        errors.push({
          stepId: node.id,
          message:
            `узел «${node.name}»: первый экран показывается ДО запуска сценария — выражения {{ … }} ` +
            'в его параметрах не сработают, впиши статичные значения',
        });
        continue;
      }
    }
    // Легаси-экран (одна JSON-строка) с выражением внутри: маскировать нечего —
    // строка целиком «выражение», статически не судим (у триггера уже ошибка выше).
    if (typeof params.screen === 'string' && params.screen !== '' && isExpression(params.screen)) {
      continue;
    }
    const parsed = screenFromNodeParams(maskScreenExpressions(params) as Record<string, unknown>);
    if (!parsed.screen) {
      errors.push({
        stepId: node.id,
        message: `узел «${node.name}»: экран собран неверно — ${parsed.errors.join('; ')}`,
      });
      continue;
    }
    nextButtonsByName.set(node.name, screenNextButtons(parsed.screen).length);
    if (isFormTrigger && screenNextButtons(parsed.screen).length === 0) {
      errors.push({
        stepId: node.id,
        message:
          `узел «${node.name}»: на первом экране нет ни одной кнопки «Продолжить сценарий» ` +
          '(kind next) — сценарий никогда не запустится',
      });
    }
    if (isFormTrigger && screenFileButtons(parsed.screen).length > 0) {
      errors.push({
        stepId: node.id,
        message:
          `узел «${node.name}»: download-кнопка «из данных» (file) недоступна на первом экране — ` +
          'файлы появляются по ходу сценария; перенеси её на узел «Экран»',
      });
    }
  }

  // Хвосты веток: браузер держит запрос, а ответить умеет только «Экран» (responseMode
  // lastNode) — ветка, обрывающаяся не-экраном, оставит человека со спиннером и честной
  // ошибкой bad_screen ПРИ УСПЕШНОМ прогоне сценария (запись создана, а человеку —
  // «не так»). Это ОШИБКИ, не предупреждения (владелец 2026-07-19, BUILDER-UX-FIXLIST
  // 1.2а: «мы за форму отвечаем — контролируем сами», публикацию не пускать). Смотрим
  // только достижимое от «Формы» (у постороннего триггера в этой же схеме свои хвосты).
  const form = forms[0];
  if (!form) return;
  const targetsByFrom = new Map<string, string[]>();
  for (const [from, byPort] of Object.entries(wf.connections)) {
    const targets: string[] = [];
    for (const outputs of Object.values(byPort)) {
      for (const conns of outputs) for (const t of conns ?? []) targets.push(t.node);
    }
    targetsByFrom.set(from, targets);
  }
  const reachable = new Set<string>([form.name]);
  const queue = [form.name];
  while (queue.length > 0) {
    for (const next of targetsByFrom.get(queue.pop()!) ?? []) {
      if (!reachable.has(next)) {
        reachable.add(next);
        queue.push(next);
      }
    }
  }
  for (const n of active) {
    if (!reachable.has(n.name) || (targetsByFrom.get(n.name) ?? []).length > 0) continue;
    if (n.type === SCREEN_NODE_TYPE) {
      if ((nextButtonsByName.get(n.name) ?? 0) > 0) {
        errors.push({
          stepId: n.id,
          message:
            `узел «${n.name}»: за экраном с кнопками «Продолжить» нет шагов — нажатие оставит ` +
            'пользователя без ответа. Добавь продолжение или сделай экран финальным (убери next-кнопки)',
        });
      }
    } else {
      errors.push({
        stepId: n.id,
        message:
          `ветка формы обрывается на шаге «${n.name}» — сценарий отработает, но пользователь в браузере ` +
          'увидит ошибку вместо результата. Закончи каждую ветку финальным «Экраном» (без кнопок «Продолжить»)',
      });
    }
  }
}

// ── Планка качества чата (HOW-IT-WORKS §16; протокол — разведка живьём на n8n 2.28.6) ──
// Чат Scriptera (страница по ссылке + виджет) зовёт вебхук триггера через свой прокси:
// работают ТОЛЬКО public:true + mode:'webhook' + authentication:'none' и responseMode
// 'streaming' (чанки шлют Агент и RespondToWebhook ≥1.5) либо 'lastNode' (весь json
// последнего узла, рендерер берёт поле output). hostedChat — чужая страница n8n,
// responseNodes живёт только в hostedChat (WebSocket) — оба через прокси не отвечают.
export function lintChatApp(wf: WorkflowJson, errors: LintIssue[], warnings: LintIssue[]): void {
  const active = wf.nodes.filter((n) => n.disabled !== true);
  const chats = active.filter((n) => n.type === CHAT_TRIGGER_TYPE);
  if (chats.length === 0) return;

  // Один триггер «Чат» на сценарий: одна ссылка, один виджет (зеркало правила формы).
  for (const extra of chats.slice(1)) {
    errors.push({
      stepId: extra.id,
      message:
        `узел «${extra.name}»: триггер «Чат» может быть только ОДИН на сценарий — у чата одна ` +
        'ссылка и один виджет',
    });
  }

  const chat = chats[0]!;
  const p = chat.parameters ?? {};
  if (p.public !== true) {
    errors.push({
      stepId: chat.id,
      message:
        `узел «${chat.name}»: чат-триггеру обязателен public: true — без него вебхук чата ` +
        'отвечает 404, страница и виджет не работают',
    });
  }
  const mode = typeof p.mode === 'string' ? p.mode : 'hostedChat'; // default узла n8n
  if (mode !== 'webhook') {
    errors.push({
      stepId: chat.id,
      message:
        `узел «${chat.name}»: mode «${mode}» — это чат-страница n8n, чат Scriptera с ней не ` +
        "работает. Поставь mode: 'webhook' (страницу и виджет даёт Scriptera)",
    });
  }
  if (p.authentication !== undefined && p.authentication !== 'none') {
    errors.push({
      stepId: chat.id,
      message:
        `узел «${chat.name}»: authentication «${String(p.authentication)}» сломает чат — доступ ` +
        "закрывает Scriptera (токен ссылки). Поставь authentication: 'none'",
    });
  }
  const opts = isObj(p.options) ? p.options : {};
  const responseMode = typeof opts.responseMode === 'string' ? opts.responseMode : 'lastNode';
  if (responseMode !== 'streaming' && responseMode !== 'lastNode') {
    errors.push({
      stepId: chat.id,
      message:
        `узел «${chat.name}»: responseMode «${responseMode}» через чат Scriptera не отвечает. ` +
        "Стандарт — options.responseMode: 'streaming' (ИИ-агент стримит ответ сам); чат без " +
        "агента — 'lastNode' (последний узел ветки отдаёт {output})",
    });
  }

  // Достижимое от триггера (все порты, как у формы) + main-выходы для хвостов веток.
  const targetsByFrom = new Map<string, string[]>();
  const mainTargetsByFrom = new Map<string, string[]>();
  for (const [from, byPort] of Object.entries(wf.connections)) {
    const all: string[] = [];
    const main: string[] = [];
    for (const [port, outputs] of Object.entries(byPort)) {
      for (const conns of outputs) {
        for (const t of conns ?? []) {
          all.push(t.node);
          if (port === 'main') main.push(t.node);
        }
      }
    }
    targetsByFrom.set(from, all);
    mainTargetsByFrom.set(from, main);
  }
  const reachable = new Set<string>([chat.name]);
  const queue = [chat.name];
  while (queue.length > 0) {
    for (const next of targetsByFrom.get(queue.pop()!) ?? []) {
      if (!reachable.has(next)) {
        reachable.add(next);
        queue.push(next);
      }
    }
  }
  const inChat = active.filter((n) => reachable.has(n.name));

  if (responseMode === 'streaming') {
    // Старый RespondToWebhook в стриминг-чате — молча пустой ответ (находка живого смока).
    for (const n of inChat) {
      if (n.type !== RESPOND_TO_WEBHOOK_TYPE || n.typeVersion >= RESPOND_STREAMING_MIN_VERSION) continue;
      errors.push({
        stepId: n.id,
        message:
          `шаг «${n.name}»: RespondToWebhook стримит в чат только с typeVersion ${RESPOND_STREAMING_MIN_VERSION} — ` +
          `у ${n.typeVersion} посетитель молча получит пустой ответ. Подними typeVersion`,
      });
    }
    const responders = inChat.filter(
      (n) =>
        n.type === AGENT_NODE_TYPE ||
        (n.type === RESPOND_TO_WEBHOOK_TYPE && n.typeVersion >= RESPOND_STREAMING_MIN_VERSION),
    );
    if (responders.length === 0) {
      errors.push({
        stepId: chat.id,
        message:
          `узел «${chat.name}»: в чате никто не отвечает — в режиме streaming чанки ответа шлют ` +
          `только ИИ-Агент и RespondToWebhook (typeVersion ${RESPOND_STREAMING_MIN_VERSION}+). Добавь агента ` +
          "с памятью диалога или переключи options.responseMode на 'lastNode'",
      });
    }
  }

  if (responseMode === 'lastNode') {
    // Ответ = ВЕСЬ json последнего узла, рендерер берёт поле output — хвост ветки, не
    // отдающий output (запись в таблицу, отправка, сам триггер без шагов), оставит
    // посетителя без ответа. Агент отдаёт {output} всегда; остальных судим по упоминанию
    // «output» в параметрах (Set/Code с полем output проходят) — лучше пропустить, чем врать.
    for (const n of inChat) {
      if ((mainTargetsByFrom.get(n.name) ?? []).length > 0 || n.type === AGENT_NODE_TYPE) continue;
      const acc: string[] = [];
      collectParamStrings(n.parameters ?? {}, acc);
      if (acc.some((s) => s.includes('output'))) continue;
      errors.push({
        stepId: n.id,
        message:
          `ветка чата заканчивается шагом «${n.name}», который не отдаёт поле output — посетитель ` +
          'не увидит ответа. Закончи ветку ИИ-агентом или узлом Set с полем output (текст ответа)',
      });
    }
  }

  // Агент без памяти диалога забывает контекст на каждой реплике — консультант «с амнезией».
  for (const agent of inChat) {
    if (agent.type !== AGENT_NODE_TYPE) continue;
    const hasMemory = active.some((m) =>
      (Object.entries(wf.connections[m.name] ?? {})).some(
        ([port, outputs]) =>
          port === 'ai_memory' &&
          outputs.some((conns) => (conns ?? []).some((t) => t.node === agent.name)),
      ),
    );
    if (!hasMemory) {
      warnings.push({
        stepId: agent.id,
        message:
          `агент «${agent.name}» без памяти диалога — каждое сообщение чата будет встречать как ` +
          'первое. Подключи к агенту узел памяти (Simple Memory); ключ сессии — sessionId из триггера',
      });
    }
  }
}

// ── Подписи «для людей» (docs/archive/SHARE-PLAN.md, Ф2) ─────────────────────────────────────
// У каждого шага должна быть заметка (notes) одной фразой простым языком: она видна на
// схеме в кабинете и уходит в публичный снимок шеринга. Одно СВОДНОЕ предупреждение на
// workflow, не по узлу — ход строителя должен оставаться дешёвым.
function lintHumanNotes(wf: WorkflowJson, warnings: LintIssue[]): void {
  const missing = wf.nodes.filter(
    (n) =>
      n.disabled !== true &&
      // Заметка-стикер САМА объяснение — просить у неё заметку бессмысленно (К15,
      // тот же шум, что и связность: стикер не шаг).
      canonicalNodeType(n.type) !== 'n8n-nodes-base.stickyNote' &&
      !(typeof n.notes === 'string' && n.notes.trim() !== ''),
  );
  if (missing.length === 0) return;
  const shown = missing.slice(0, 8).map((n) => `«${n.name}»`);
  const tail = missing.length > shown.length ? ` и ещё ${missing.length - shown.length}` : '';
  warnings.push({
    stepId: missing[0]!.id,
    message:
      `у шаг${missing.length > 1 ? 'ов' : 'а'} ${shown.join(', ')}${tail} нет заметки (notes) — ` +
      'дай каждому одну фразу простым языком, что здесь происходит ' +
      '(«Запускается, когда боту приходит сообщение»): подписи видны пользователю на схеме',
  });
}

// ── Заметки-макеты и чужие бренды в подписях (MINIMAL-PRODUCT, претензия владельца 31.07) ──
// История. Шаблоны, конвертированные из чужих конструкторов, несли в notes ЖУРНАЛ КОНВЕРТАЦИИ:
// «Макет: карточка клиента в разработке», «в выгрузке BotHelp критерий ПУСТ (rules: [])».
// Написано это было для нас, а notes кабинет печатает подписью на карточке шага — и клиент
// читал на своей схеме «Макет … в разработке» и чужие бренды. Одного «вычистили данные» мало:
// сценарий клиента — СНИМОК шаблона, снятый в момент создания, и всё, что попало в шаблон,
// живёт у клиента вечно. Поэтому замок здесь, до установки.
// Предупреждение СБОРЩИЦКОЕ (dev): чинит его тот, кто правит шаблон, а не клиент.
const STUB_NOTE_MARKERS = ['Макет:', 'в разработке', 'BotHelp', 'ChatPlace'];

/** Заметка-макет: журнал конвертации вместо подписи «для людей». */
export function isStubNote(note: unknown): boolean {
  if (typeof note !== 'string') return false;
  return STUB_NOTE_MARKERS.some((m) => note.includes(m));
}

function lintStubNotes(wf: WorkflowJson, warnings: LintIssue[]): void {
  const bad = wf.nodes.filter((n) => isStubNote(n.notes));
  if (bad.length === 0) return;
  const shown = bad.slice(0, 6).map((n) => `«${n.name}»`);
  const tail = bad.length > shown.length ? ` и ещё ${bad.length - shown.length}` : '';
  warnings.push({
    stepId: bad[0]!.id,
    message:
      `подпись шаг${bad.length > 1 ? 'ов' : 'а'} ${shown.join(', ')}${tail} — заметка-макет ` +
      '(«Макет: …», «в разработке», имя чужого сервиса): это внутренний журнал переноса, а ' +
      'клиент читает его на карточке шага. Замени одной фразой простым языком, что здесь ' +
      'происходит; если механика правда не готова — так и напиши, без чужих брендов',
  });
}

// ── Вход движков Scriptera (docs/archive/BUILDER-REPAIR-PLAN.md, этап 2) ────────────────────
// Класс бага «не смог получить первый кадр»: строитель совал в inputUrls выдуманное
// поле ({{ $json.url }} после telegram getFile), которого в данных нет, — шаг падал
// всегда. Файл от телеграм-шага существует ТОЛЬКО в binary (download:true); публичной
// ссылки на него нет. Ловим до деплоя: (а) оба входа пусты; (б) выражение в inputUrls,
// когда непосредственный апстрим — Telegram (файл надо брать из binary).
const TELEGRAM_NODE_TYPES = new Set(['n8n-nodes-base.telegram', TELEGRAM_TRIGGER_TYPE]);

/** Непосредственные предшественники каждого узла по всем портам connections. */
function upstreamMap(wf: WorkflowJson): Map<string, Set<string>> {
  const map = new Map<string, Set<string>>();
  for (const [from, byPort] of Object.entries(wf.connections)) {
    for (const outputs of Object.values(byPort)) {
      for (const targets of outputs) {
        for (const t of targets ?? []) {
          (map.get(t.node) ?? map.set(t.node, new Set()).get(t.node)!).add(from);
        }
      }
    }
  }
  return map;
}

// Прямые вызовы Telegram и несуществующий глобал $credentials (находка прогона 2026-07-06:
// строитель построил httpRequest на api.telegram.org/bot{{$credentials…}}/getFile, чтобы
// получить ссылку на видео для другой ветки). Файл в другой ветке достают телеграм-узлом
// по file_id из контекста (download:true → binary), а не самодельным URL с токеном.
function lintTelegramDirectApi(wf: WorkflowJson, errors: LintIssue[]): void {
  for (const n of wf.nodes) {
    if (n.disabled === true) continue;
    const acc: string[] = [];
    collectParamStrings(n.parameters ?? {}, acc);
    if (acc.some((s) => s.includes('api.telegram.org'))) {
      errors.push({
        stepId: n.id,
        message:
          `узел «${n.name}» зовёт api.telegram.org напрямую — запрещено и не работает ` +
          '(токен бота в выражениях недоступен). Файл Telegram доставай узлом Telegram ' +
          '(resource=file, download:true) по file_id — он придёт в binary; file_id между ' +
          'ветками храни в Data table',
      });
    } else if (acc.some((s) => s.includes('$credentials'))) {
      errors.push({
        stepId: n.id,
        message:
          `узел «${n.name}» использует $credentials — такого глобала в выражениях n8n НЕТ, ` +
          'выражение упадёт. Секреты доступны только узлам через подключённый аккаунт',
      });
    }
  }
}

// Data table (BUILDER-REPAIR-PLAN этап 4, находки живого прогона): строки адресуют
// таблицу по имени, но саму таблицу никто не создаёт → первый запуск падает. Канон —
// шаг resource=table/operation=create с options.createIfNotExists=true в этой же схеме.
const DATA_TABLE_TYPE = 'n8n-nodes-base.dataTable';

function lintDataTables(wf: WorkflowJson, warnings: LintIssue[]): void {
  const active = wf.nodes.filter((n) => n.disabled !== true && n.type === DATA_TABLE_TYPE);
  if (active.length === 0) return;
  const created = new Set(
    active
      .filter((n) => n.parameters?.resource === 'table' && n.parameters?.operation === 'create')
      .map((n) => (typeof n.parameters?.tableName === 'string' ? n.parameters.tableName : '')),
  );
  for (const n of active) {
    const p = n.parameters ?? {};
    // create без описания колонок создаёт пустышку — upsert/get по колонкам упадёт
    // (тап-тест прода 2026-07-07: «Column(s) chat_id do not exist»).
    if (p.resource === 'table' && p.operation === 'create') {
      const colDef = (p.columns as { column?: unknown[] } | undefined)?.column;
      if (!Array.isArray(colDef) || colDef.length === 0) {
        warnings.push({
          stepId: n.id,
          message:
            `шаг «${n.name}» создаёт таблицу без колонок — писать/искать в ней будет нечего. ` +
            'Заполни columns: {column:[{name,type},…]} по полям, которые пишет upsert',
        });
      }
      continue;
    }
    if (p.resource !== 'row') continue;
    // chat_id числом с одной стороны и строкой с другой — молчаливое «не найдено»
    // (тап-тест 2026-07-07). Ловим выражение с chat.id/chatId без приведения к строке.
    {
      const acc: string[] = [];
      collectParamStrings(p, acc);
      const bad = acc.some(
        (v) =>
          isExpression(v) &&
          /(chat\.id|chatId)\s*}}/.test(v) &&
          !v.includes('.toString()') &&
          !v.includes('String('),
      );
      if (bad) {
        warnings.push({
          stepId: n.id,
          message:
            `шаг «${n.name}»: chat_id в выражении без .toString() — число при записи и строка ` +
            'при чтении не совпадут, и контекст «не найдётся». Приводи к строке с обеих сторон',
        });
      }
    }
    const rl = p.dataTableId as { mode?: string; value?: unknown } | undefined;
    if (rl?.mode === 'name' && typeof rl.value === 'string' && !created.has(rl.value)) {
      warnings.push({
        stepId: n.id,
        message:
          `шаг «${n.name}» пишет/читает таблицу «${rl.value}» по имени, но шага её создания в схеме нет — ` +
          'на чистом сервере первый запуск упадёт. Добавь узел Data table (resource=table, ' +
          'operation=create, tableName и колонки, options.createIfNotExists=true) перед первым обращением',
      });
    }
  }
}

function lintEngineInputs(wf: WorkflowJson, errors: LintIssue[]): void {
  const engines = wf.nodes.filter((n) => n.disabled !== true && ENGINE_NODE_TYPES.has(n.type));
  if (engines.length === 0) return;
  const upstream = upstreamMap(wf);
  const typeByName = new Map(wf.nodes.map((n) => [n.name, n.type]));
  for (const n of engines) {
    const p = n.parameters ?? {};
    const binarySpec = typeof p.inputBinaryProperties === 'string' ? p.inputBinaryProperties.trim() : '';
    const urls = typeof p.inputUrls === 'string' ? p.inputUrls.trim() : '';
    if (binarySpec === '' && urls === '') {
      errors.push({
        stepId: n.id,
        message:
          `шаг «${n.name}»: вход не задан — упадёт при каждом запуске. Файл от предыдущего ` +
          'шага передавай через binary (inputBinaryProperties:"data"); inputUrls — только для ' +
          'настоящих публичных ссылок',
      });
      continue;
    }
    // Data table не передаёт binary дальше: движок с binary-входом сразу после него
    // гарантированно без файла (прод-прогон 2026-07-07: «Создать таблицу контекста»
    // между триггером и «Извлечь первый кадр» терял видео).
    if (binarySpec !== '') {
      const froms = [...(upstream.get(n.name) ?? [])];
      if (froms.length > 0 && froms.every((name) => typeByName.get(name) === DATA_TABLE_TYPE)) {
        errors.push({
          stepId: n.id,
          message:
            `шаг «${n.name}» ждёт файл (binary), но стоит сразу после Data table — тот файлы ` +
            'дальше не передаёт, вход будет пуст. Переставь шаги: сначала работа с файлом, ' +
            'потом таблица',
        });
      }
    }
    if (binarySpec === '' && isExpression(urls)) {
      const fromTelegram = [...(upstream.get(n.name) ?? [])].some((name) =>
        TELEGRAM_NODE_TYPES.has(typeByName.get(name) ?? ''),
      );
      if (fromTelegram) {
        errors.push({
          stepId: n.id,
          message:
            `шаг «${n.name}»: файл из Telegram существует только в binary — публичной ссылки ` +
            'на него нет, выражение в inputUrls упадёт или отдаст пустоту. Включи у ' +
            'телеграм-шага download:true, поставь inputBinaryProperties:"data" и очисти inputUrls',
        });
      }
    }
  }
}

// ── Запрет служебного API n8n (docs/archive/BUILDER-REPAIR-PLAN.md, этап 1) ─────────────────
// Строитель хранил контекст httpRequest-ом в служебный API инстанса
// ($env.N8N_EDITOR_BASE_URL || localhost:5678 + кред n8nApi, зашитый ID таблицы) —
// хрупко и течёт в кабинет. Канон — узел «Data table». Сигналы точечные, чтобы не
// зацепить честные внешние API с путём /api/v1: env-переменная, локальный адрес
// инстанса, credential n8nApi.
const INTERNAL_API_SIGNALS = ['N8N_EDITOR_BASE_URL', 'localhost:5678', '127.0.0.1:5678'];

function lintInternalN8nApi(wf: WorkflowJson, errors: LintIssue[]): void {
  for (const n of wf.nodes) {
    if (n.disabled === true) continue;
    const params = n.parameters ?? {};
    const acc: string[] = [];
    collectParamStrings(params, acc);
    const hit =
      params.nodeCredentialType === 'n8nApi' ||
      acc.some((s) => INTERNAL_API_SIGNALS.some((sig) => s.includes(sig)));
    if (hit) {
      errors.push({
        stepId: n.id,
        message:
          `узел «${n.name}» ходит в служебный API сервера ($env.N8N_EDITOR_BASE_URL / localhost / ` +
          'кред n8nApi) — это запрещено и сломается. Контекст между шагами храни узлом ' +
          '«Data table» (n8n-nodes-base.dataTable): таблицу создаёт сам сценарий ' +
          '(resource=table, operation=create, options.createIfNotExists=true), строки — ' +
          'upsert/get по chat_id. Эталон — в карточке узла Telegram Trigger (get_node)',
      });
    }
  }
}

// ── Валидация условий If/Switch/Filter (BUILDER-GUARDRAILS-PLAN этап 1) ──────────────
// Живой инцидент 2026-07-07 («слепая развилка» видеохук-бота): строитель написал
// операцию isNotEmpty, которой у ФИЛЬТРОВ n8n не существует (есть notEmpty; isNotEmpty —
// функция ВЫРАЖЕНИЙ, модель перепутала похожие имена). n8n молча считает неизвестную
// операцию/тип «не совпало» (default → false в executeFilterCondition) — ветка слепнет
// без единой ошибки, n8n-mcp validate_workflow это тоже пропускает. Список — зеркало
// switch'ей n8n-workflow dist/node-parameters/filter-parameter.js (n8n 2.28.6);
// при апгрейде n8n сверить с исходником.
const FILTER_OPERATIONS: Record<string, ReadonlySet<string>> = {
  string: new Set([
    'empty', 'notEmpty', 'equals', 'notEquals', 'contains', 'notContains',
    'startsWith', 'notStartsWith', 'endsWith', 'notEndsWith', 'regex', 'notRegex',
  ]),
  number: new Set(['empty', 'notEmpty', 'equals', 'notEquals', 'gt', 'lt', 'gte', 'lte']),
  dateTime: new Set(['equals', 'notEquals', 'after', 'before', 'afterOrEquals', 'beforeOrEquals']),
  boolean: new Set(['empty', 'notEmpty', 'true', 'false', 'equals', 'notEquals']),
  array: new Set([
    'contains', 'notContains', 'lengthEquals', 'lengthNotEquals',
    'lengthGt', 'lengthLt', 'lengthGte', 'lengthLte', 'empty', 'notEmpty',
  ]),
  object: new Set(['empty', 'notEmpty']),
};

// Частая путаница LLM: имена функций выражений вместо операций фильтра
// (isNotEmpty → notEmpty, isEmpty → empty). Подсказка режет круг починки до одного шага.
function suggestFilterOperation(type: string, op: string): string | null {
  const allowed = FILTER_OPERATIONS[type];
  if (!allowed) return null;
  const lower = op.toLowerCase();
  for (const a of allowed) {
    const al = a.toLowerCase();
    if (al === lower || `is${al}` === lower || al === `is${lower}`) return a;
  }
  return null;
}

// Фильтр-значение узнаём по ФОРМЕ ({combinator, conditions:[…]}) на любой глубине
// параметров — так выглядит параметр типа filter у If, Switch (rules) и Filter.
function walkFilterConditions(
  value: unknown,
  visit: (cond: Record<string, unknown>) => void,
): void {
  if (Array.isArray(value)) {
    for (const v of value) walkFilterConditions(v, visit);
    return;
  }
  if (!isObj(value)) return;
  if (typeof value.combinator === 'string' && Array.isArray(value.conditions)) {
    for (const c of value.conditions) {
      if (isObj(c)) visit(c);
    }
  }
  for (const v of Object.values(value)) walkFilterConditions(v, visit);
}

export function lintFilterConditions(wf: WorkflowJson, errors: LintIssue[]): void {
  for (const node of wf.nodes) {
    if (node.disabled === true) continue;
    walkFilterConditions(node.parameters ?? {}, (cond) => {
      const op = isObj(cond.operator) ? cond.operator : null;
      if (!op) return;
      const type = typeof op.type === 'string' ? op.type : '';
      const operation = typeof op.operation === 'string' ? op.operation : '';
      if (!type || !operation) return;
      const allowed = FILTER_OPERATIONS[type];
      if (!allowed) {
        errors.push({
          stepId: node.id,
          message:
            `узел «${node.name}»: тип условия «${type}» не существует — n8n молча посчитает такое ` +
            `условие ложным (ветка не сработает). Допустимые типы: ${Object.keys(FILTER_OPERATIONS).join(', ')}`,
        });
        return;
      }
      if (!allowed.has(operation)) {
        const hint = suggestFilterOperation(type, operation);
        errors.push({
          stepId: node.id,
          message:
            `узел «${node.name}»: операции «${operation}» у условий типа ${type} не существует — ` +
            'n8n молча посчитает условие ложным (ветка не сработает, ошибки не будет). ' +
            `${hint ? `Ты имел в виду «${hint}»? ` : ''}` +
            `Допустимые операции ${type}: ${[...allowed].join(', ')}. Не выдумывай имена — ` +
            'функции выражений (.isNotEmpty() и т.п.) операциями фильтра НЕ являются',
        });
      }
    });
  }
}


/**
 * Пилюли карточки «Условие» (У0 + У12): пустая пилюля — ошибка запуска, пилюля без своей
 * линии — предупреждение. Пустая группа условий пропускает ВСЕХ по своей ветке, а понять
 * это по карточке нельзя, поэтому запуск держим.
 *
 * Виноват в обоих случаях КЛИЕНТ (добавил карточку и не выбрал, что проверять; задал
 * условие и не дотянул линию) — значит говорим ему прямо и человеческими словами
 * (запрет №7 CLAUDE.md); на самой карточке пустая пилюля стоит красной плашкой «Условие
 * не задано» (step-content.ts). Чужие развилки и «Если» (условия не нашей формы —
 * сравнения строк, чисел) не трогаем: readChatSwitch на них молчит.
 */
function lintEmptyConditions(wf: WorkflowJson, errors: LintIssue[], warnings: LintIssue[]): void {
  const linked = new Map<string, Set<number>>(); // имя шага → занятые выходы
  for (const [fromName, byPort] of Object.entries(wf.connections ?? {})) {
    const outputs = byPort?.main;
    if (!Array.isArray(outputs)) continue;
    const used = new Set<number>();
    outputs.forEach((targets, i) => {
      if ((targets ?? []).length > 0) used.add(i);
    });
    linked.set(fromName, used);
  }
  for (const node of wf.nodes) {
    if (node.disabled === true) continue;
    const sw = readChatSwitch(node);
    if (!sw) continue;
    const used = linked.get(node.name) ?? new Set<number>();
    sw.groups.forEach((g, i) => {
      const where = sw.groups.length > 1 ? ` (условие ${i + 1})` : '';
      if (g.rows.length === 0) {
        errors.push({
          stepId: node.id,
          message:
            `шаг «${node.name}»${where}: условие не задано — сейчас шаг пропускает всех подряд. ` +
            'Откройте его настройки и выберите, что проверять',
        });
        return;
      }
      // Пилюля без своей линии (У12): условие задано, а идти после него некуда — ветка
      // обрывается. Запуск не держим (остальные ветки работают) и КЛИЕНТУ не показываем:
      // ветка «подошло — и на этом всё» бывает задумана нарочно, и в наших же живых
      // шаблонах их семь (сверка 12.08). Значок «!» на здоровой карточке готовой схемы —
      // ровно та дыра Д8, из-за которой сборщицкие предупреждения помечают dev
      // (запрет №7 CLAUDE.md: чужой брак клиенту не показываем). Предупреждение видят
      // сборщик и дежурный.
      if (!used.has(i)) {
        warnings.push({
          stepId: node.id,
          dev: true,
          message:
            `шаг «${node.name}»${where}: условие подошло, а дальше ничего нет — ветка никуда ` +
            'не ведёт. Добавь шаг после неё или убери условие',
        });
      }
    });
  }
}

/**
 * Условие «человек отвечает на просьбу о номере» БЕЗ самой просьбы (владелец 13.08).
 * Условие смотрит на пометку, которую ставит одно-единственное действие — «Запросить
 * номер телефона»; убрали его (или выключили) — пометке взяться неоткуда, ветка не
 * сработает НИКОГДА, и бот молча встанет на этом месте.
 *
 * Виноват КЛИЕНТ (убрал шаг из своей схемы), значит запуск держим и говорим ему прямо,
 * что доделать — без техножаргона и без служебного имени пометки (запрет №7 CLAUDE.md).
 * То же самое видно в панели условия красной строкой.
 */
function lintPhoneWaitConditions(wf: WorkflowJson, errors: LintIssue[]): void {
  if (phoneAskPresent(wf.nodes)) return;
  for (const node of wf.nodes) {
    if (node.disabled === true) continue;
    const sw = readChatSwitch(node);
    if (!sw) continue;
    const hit = sw.groups.some((g) => g.rows.some((r) => condRowPhoneWait(r)));
    if (!hit) continue;
    errors.push({
      stepId: node.id,
      message:
        `шаг «${node.name}»: условие ждёт ответ на просьбу о номере, но в схеме нет шага, ` +
        'который просит номер, — добавьте действие «Запросить номер телефона»',
    });
  }
}

/**
 * Пер-узловые проверки параметров против каталога (кусок lintWorkflow, вынесен):
 * несуществующие/скрытые параметры, форма вложенных структур, значения options,
 * обязательные поля. Экспортирован отдельно, чтобы панель шага могла пересчитывать
 * «Что доделать» живьём при правке (владелец 2026-07-17), без графовых проверок.
 */
export function lintNodeParams(
  node: WorkflowNode,
  info: CatalogNodeInfo,
  errors: LintIssue[],
  warnings: LintIssue[],
): void {
  {
    const params = node.parameters ?? {};
    // Дубли имён сохраняем (у telegram «operation» описан на каждый resource отдельно);
    // default — из ВИДИМОГО определения (единый определитель resolveParamDefaults, Ф11:
    // «первое по списку» расходилось с формой и n8n — Gmail уезжал зелёным с пустыми
    // обязательными полями выбранной операции).
    const defsByName = new Map<string, CatalogParam[]>();
    for (const p of info.params) {
      const list = defsByName.get(p.name);
      if (list) list.push(p);
      else defsByName.set(p.name, [p]);
    }
    const defaults = resolveParamDefaults(info.params, params);

    const unknown = Object.keys(params).filter((key) => !defsByName.has(key));
    if (unknown.length > 0) {
      errors.push({
        stepId: node.id,
        message:
          `параметр${unknown.length > 1 ? 'ы' : ''} ${unknown.map((k) => `«${k}»`).join(', ')} ` +
          `не существу${unknown.length > 1 ? 'ют' : 'ет'} у узла ${info.displayName}. ` +
          `Допустимые параметры: ${allowedParamsHint(info)}. ` +
          'Не выдумывай имена — бери из этого списка, структуру сложных полей смотри в get_node/docs_get',
      });
    }

    // Заданные параметры: скрытый при текущих значениях → «не действует»; значение
    // options/multiOptions — только из списка видимых определений (пары дискриминаторов:
    // resource=chat + operation=sendPhoto режется именно здесь).
    for (const [key, value] of Object.entries(params)) {
      const defs = defsByName.get(key);
      if (!defs) continue; // несуществующие уже отработали выше
      const visible = defs.filter((d) => paramVisible(d, params, defaults));
      if (visible.length === 0) {
        warnings.push({
          stepId: node.id,
          message:
            `узел ${info.displayName}: параметр «${key}» скрыт при текущих значениях и НЕ действует` +
            `${showCondsHint(defs)} — убери его или поменяй условия`,
        });
        continue;
      }
      // Полная сверка формы вложенных структур с каталогом (BUILDER-VERIFY-PLAN этап 1):
      // fixedCollection (обёртка, имена групп, ключи элементов), collection (известные
      // ключи, рекурсия вглубь), resourceLocator ({mode, value}), resourceMapper
      // ({mappingMode, value}). Ошибка несёт мини-справку «как надо» из каталога.
      // root — корень узла для «/…»- и @version-условий подполей (Ф13(2)(4)).
      lintParamShape(
        info.displayName,
        node.id,
        key,
        visible,
        value,
        errors,
        new Set(defsByName.keys()),
        { params: { ...Object.fromEntries(defaults), ...params }, version: node.typeVersion },
      );
      const allowed = collectOptionValues(visible);
      if (!allowed) continue;
      const candidates = (Array.isArray(value) ? value : [value]).filter(
        (v): v is string | number | boolean =>
          (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') && !isExpression(v),
      );
      for (const v of candidates) {
        if (!allowed.has(v)) {
          errors.push({
            stepId: node.id,
            message:
              `узел ${info.displayName}: значение «${String(v)}» недопустимо для «${key}» при текущих ` +
              `параметрах узла. Допустимые: ${optionListHint(allowed)}`,
          });
        }
      }
    }

    // Обязательные: с учётом default и видимости; пустой массив = не заполнено.
    const reported = new Set<string>();
    for (const p of info.params) {
      if (!p.required || reported.has(p.name)) continue;
      if (!paramVisible(p, params, defaults)) continue;
      const value = p.name in params ? params[p.name] : p.default;
      if (isEmptyRequiredParamValue(p, value)) {
        reported.add(p.name);
        errors.push({ stepId: node.id, message: requiredParamMessage(p) });
      }
    }

    // BUILDER-UX-FIXLIST 2.6: имя базы (коллекции) Qdrant человек придумывает САМ
    // (вкладка «ID»; база создаётся при первой записи) — формат проверяем статически,
    // иначе поломка всплывёт только в рантайме. Только руками вписанное имя (mode id):
    // выбранное из списка уже существует, выражение оценит рантайм. Текст содержит
    // «недопустимо для «qdrantCollection»» — панель шага подсветит само поле.
    if (node.type === QDRANT_VECTOR_STORE_TYPE) {
      const rl = params.qdrantCollection;
      if (isObj(rl) && rl.mode === 'id' && typeof rl.value === 'string') {
        const name = rl.value.trim();
        if (name !== '' && !isExpression(name) && !QDRANT_COLLECTION_NAME_RE.test(name)) {
          errors.push({
            stepId: node.id,
            message:
              `узел ${info.displayName}: имя базы «${name}» недопустимо для «qdrantCollection» — ` +
              'латиница, цифры, дефис и подчёркивание, не длиннее 48 знаков ' +
              '(база с таким именем создастся сама при первой записи)',
          });
        }
      }
    }
  }
}

export function lintWorkflow(
  wf: WorkflowJson,
  catalog: Map<string, CatalogNodeInfo>,
  opts?: WorkflowLintOptions,
): LintResult {
  const errors: LintIssue[] = [];
  const warnings: LintIssue[] = [];
  /** Предупреждения СБОРЩИКА (Д8): помечаются dev и до клиента не доезжают. */
  const dev: LintIssue[] = [];

  for (const node of wf.nodes) {
    if (node.disabled === true) continue; // выключенный узел n8n не исполняет
    // Заметка-стикер не исполняется — её версия и поля никого не касаются, а ОШИБКА
    // линта (в отличие от предупреждения) доезжает до клиента И отбивает запуск
    // (`lint_failed`, scenario-run.ts). Ровно так все шесть шаблонов минимума стали
    // незапускаемыми: заметки лежали с typeVersion 2 при версии 1 у n8n, и кабинет
    // отказывал «доделайте шаги: Заметка 1, Заметка 2» — шаги, которых человек не
    // правит. Третье место того же правила К15 (связность и заметка-у-заметки — выше).
    if (canonicalNodeType(node.type) === 'n8n-nodes-base.stickyNote') continue;
    // Счётчик «Учёт шага» — кухня сборки (step-guard.ts): в канон он попадает только
    // синком из редактора, клиент его не правит и починить не может. Любая находка
    // линта по нему (нет в каталоге, версия, параметры) — вина платформы, клиенту её
    // не показываем и запуск не держим (запрет №7).
    if (canonicalNodeType(node.type) === GUARD_NODE_TYPE) continue;
    const info = catalog.get(node.type);
    if (!info) {
      errors.push({
        stepId: node.id,
        message: `узел «${node.type}» отсутствует в каталоге — проверь тип через search_nodes`,
      });
      continue;
    }
    if (typeof info.defaultVersion === 'number' && node.typeVersion > info.defaultVersion) {
      errors.push({
        stepId: node.id,
        message:
          `узел ${info.displayName}: typeVersion ${node.typeVersion} новее, чем поддерживает ` +
          `сервер (максимум ${info.defaultVersion}) — поставь ${info.defaultVersion}`,
      });
    }
    lintNodeParams(node, info, errors, dev);
  }

  if (opts?.models) {
    errors.push(...lintScripteraModels(wf, opts.models));
  }

  // Приёмка К0, дыра Д8: предупреждения делятся на КЛИЕНТСКИЕ (их кабинет выносит на
  // карточки — канал, невыбранный файл вложения, отсутствие запуска) и СБОРЩИЦКИЕ
  // (качество схемы, заметки, связность, данные между шагами) — последние адресованы
  // боту/дежурному и клиенту не показываются: иначе «!» повисает на здоровых карточках
  // живых шаблонов (стикеры-заметки, задуманная альтернативная ветка CRM).
  lintConnectivity(wf, catalog, errors, dev);
  lintAiAttachments(wf, catalog, errors);
  lintEmbeddingModels(wf, catalog, opts?.embeddings, errors);
  lintSingleTelegramTrigger(wf, errors);
  if (opts?.accountCreds) lintBotAccount(wf, opts.accountCreds, errors);
  lintSubscribeChannels(wf, opts?.tgChannels, errors);
  lintBotQuality(wf, dev);
  lintChannels(wf, warnings);
  lintChatAttachments(wf, warnings);
  lintFormApp(wf, errors, dev);
  lintChatApp(wf, errors, dev);
  lintFilterConditions(wf, errors);
  lintEmptyConditions(wf, errors, warnings);
  lintPhoneWaitConditions(wf, errors);
  lintInternalN8nApi(wf, errors);
  lintTelegramDirectApi(wf, errors);
  lintEngineInputs(wf, errors);
  lintDataTables(wf, dev);
  lintDataFlow(wf, dev);
  lintHumanNotes(wf, dev);
  lintStubNotes(wf, dev);

  if (wf.nodes.length === 0) {
    warnings.push({ stepId: '', message: 'workflow пуст' });
  } else if (!wf.nodes.some((n) => n.disabled !== true && catalog.get(n.type)?.isTrigger)) {
    // Ф0 (BOT-INTEGRATIONS, владелец 02.08): схема БЕЗ настроенного запуска существовать
    // не должна — это ОШИБКА, а не предупреждение: она отбивает запуск и деплой, а не
    // молчит до боя. Выключенный запуск НЕ считается: он не сработает, включить схему
    // с ним всё равно нельзя. Пустой черновик (nodes: []) остаётся предупреждением —
    // его только начали собирать, ругаться не на что.
    errors.push({
      stepId: wf.nodes[0]!.id,
      message:
        'в схеме нет шага запуска: сценарию нечем запускаться, включить его нельзя — ' +
        'добавь запуск (шаг «Начало», webhook/расписание/событие)',
    });
  }

  for (const w of dev) warnings.push({ ...w, dev: true });
  return { ok: errors.length === 0, errors, warnings };
}

// ── Гашение подписей n8n (позиционирование, /CLAUDE.md: клиент не видит n8n) ─────────
// Отправляющие ноды (Telegram/Slack/Gmail/Discord/…) дописывают к сообщению «sent
// automatically with n8n»; опция включена ПО УМОЛЧАНИЮ — отсутствие ключа = подпись
// есть. Перед деплоем принудительно проставляем false в каждом top-level
// collection-параметре узла, среди опций которого есть такой ключ (у telegram их два:
// options и additionalFields). Лишний ключ в невидимой при текущей операции коллекции
// n8n просто не читает — безвредно.
const ATTRIBUTION_KEYS = ['appendAttribution', 'includeLinkToWorkflow'];

function stripN8nAttribution(
  wf: WorkflowJson,
  catalog: Map<string, CatalogNodeInfo>,
): WorkflowJson {
  const nodes = wf.nodes.map((node) => {
    const info = catalog.get(node.type);
    if (!info) return node;
    let patched: Record<string, unknown> | null = null;
    for (const p of info.params) {
      if (!Array.isArray(p.options)) continue;
      const keys = ATTRIBUTION_KEYS.filter((k) =>
        (p.options as Array<{ name?: unknown }>).some((o) => o && typeof o === 'object' && o.name === k),
      );
      if (keys.length === 0) continue;
      const base: Record<string, unknown> = patched ?? node.parameters ?? {};
      const prev = base[p.name];
      const coll =
        prev && typeof prev === 'object' && !Array.isArray(prev)
          ? { ...(prev as Record<string, unknown>) }
          : {};
      for (const k of keys) coll[k] = false;
      patched = { ...base, [p.name]: coll };
    }
    return patched ? { ...node, parameters: patched } : node;
  });
  return { ...wf, nodes };
}

// Триггер Telegram младше 1.3 при активации НЕ сбрасывает накопленные апдейты
// (drop_pending_updates появился в n8n с typeVersion 1.3): выключил сценарий →
// написал боту → включил → прилетает старьё (владелец, 2026-07-11). На деплое
// поднимаем версию — параметры 1.1→1.3 совместимы, меняется только поведение вебхука.
const TELEGRAM_TRIGGER_DROP_VERSION = 1.3;
function bumpTelegramTriggers(wf: WorkflowJson): WorkflowJson {
  const nodes = wf.nodes.map((node) => {
    if (node.type !== TELEGRAM_TRIGGER_TYPE) return node;
    const v = typeof node.typeVersion === 'number' ? node.typeVersion : 1;
    return v >= TELEGRAM_TRIGGER_DROP_VERSION ? node : { ...node, typeVersion: TELEGRAM_TRIGGER_DROP_VERSION };
  });
  return { ...wf, nodes };
}

/**
 * Тот же подъём для УЖЕ развёрнутого воркфлоу (тумблер включения, бэклог 2026-07-11):
 * деплой новые сценарии лечит (bumpTelegramTriggers выше), а старые включаются как есть
 * и продолжают получать накопленное. Вход — сырой workflow-JSON публичного API; выход —
 * тело PUT /workflows/:id (name+nodes+connections+settings) или null, когда поднимать
 * нечего (лишний PUT инстансу не шлём).
 */
export function telegramTriggerBumpPatch(raw: Record<string, unknown>): {
  name: string;
  nodes: Array<Record<string, unknown>>;
  connections: Record<string, unknown>;
  settings: Record<string, unknown>;
} | null {
  if (!Array.isArray(raw.nodes)) return null;
  let changed = false;
  const nodes = (raw.nodes as Array<Record<string, unknown>>).map((n) => {
    if (!n || typeof n !== 'object' || n.type !== TELEGRAM_TRIGGER_TYPE) return n;
    const v = typeof n.typeVersion === 'number' ? n.typeVersion : 1;
    if (v >= TELEGRAM_TRIGGER_DROP_VERSION) return n;
    changed = true;
    return { ...n, typeVersion: TELEGRAM_TRIGGER_DROP_VERSION };
  });
  if (!changed) return null;
  const rec = (v: unknown): Record<string, unknown> =>
    v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
  return {
    name: typeof raw.name === 'string' && raw.name ? raw.name : 'Сценарий Scriptera',
    nodes,
    connections: rec(raw.connections),
    settings: rec(raw.settings),
  };
}

/**
 * Приставка клиента на ОБЩЕМ инстансе (SHARED-N8N-PLAN; исполнитель у всех один):
 * workflow, креды, таблицы и вебхук-пути живут в n8n как `<userId>_<имя>` — по ней
 * держится забор между клиентами. Добавляет/снимает приставку только наша прослойка,
 * клиент видит чистые имена. Единственный источник формата — эта функция.
 */
export function execPrefixOf(userId: string): string {
  return `${userId}_`;
}

/**
 * Креды инстанса, видимые exec-клиенту (SHARED-N8N Ф2): на общем инстансе — ТОЛЬКО со
 * своей приставкой (чужие не видим и НЕ привязываем). prefix '' (личный инстанс) —
 * список как есть. С Ф4 списки режутся реестром (filterExecCredsById); приставочный
 * вариант остался запасным забором для путей без доступа к БД.
 *
 * INVARIANT(scriptera): служебные креды Scriptera («Scriptera AI», «Scriptera AI (модели)»,
 * «Scriptera») на общем инстансе НЕ общие — они пер-клиент (в данных креда лежит
 * КЛИЕНТСКИЙ ключ шлюза, с его баланса списывают ИИ-шаги; заводит ensureScripteraExecCreds
 * с приставкой `<userId>_` и рядом в реестре). Пропускать их «как общие» нельзя: список
 * попадает в bindCredentials/buildDeployWorkflow, а те берут ПЕРВЫЙ кред типа — и бот
 * клиента уезжал с ключом Scriptera ДРУГОГО клиента (инцидент 3eb6c926, 17–18.09.2026:
 * контакты и списания легли бы чужому). Причина: до 18.09 фильтр был
 * `своя приставка || isScripteraManagedCred`, а у scripteraAiApi узнавание идёт по одному
 * типу — чужие «<other>_Scriptera AI» проходили все. Канон: docs/HOW-IT-WORKS.md §6.
 */
export function filterExecCreds<T extends { name: string; type: string }>(creds: T[], prefix: string): T[] {
  if (!prefix) return creds;
  return creds.filter((c) => c.name.startsWith(prefix));
}

/**
 * То же, но по реестру «кред → чей» (SHARED-N8N Ф4, exec_cred_owners): ownIds — id кредов
 * юзера на этом инстансе (@scriptera/db execCredOwnerIds). Реестр — первоисточник (мы
 * сами пишем ряд при создании), приставка в имени больше не решает принадлежность:
 * кред с чужой/битой приставкой, заведённый мимо прослойки, сюда не пролезет.
 * Служебные креды Scriptera — тоже только свои (см. инвариант у filterExecCreds): свои
 * в реестре есть всегда (регистрирует создание), чужие — чужой ключ шлюза. Fail-closed.
 */
export function filterExecCredsById<T extends { id: string; name: string; type: string }>(
  creds: T[],
  ownIds: ReadonlySet<string>,
): T[] {
  return creds.filter((c) => ownIds.has(c.id));
}

// ── Забор общего инстанса (SHARED-N8N Ф2: «валидатор при сохранении», три проверки) ──
// Канон: docs/SHARED-N8N-PLAN.md «Правила». Проверяется ГОТОВАЯ деплой-сборка —
// buildDeployWorkflow зовёт забор сам, мимо него workflow на общий инстанс не уезжает:
//   1) ссылки на таблицы/сценарии — только свои (по приставке; принадлежность по голому
//      id проверить нечем до реестра Ф4 — отказ);
//   2) креды — по внутреннему id против нашего списка (filterExecCreds: только своя
//      приставка, служебные Scriptera тоже свои); чужой id, прописанный в JSON руками, bindWorkflowCredentials
//      не перезаписывает — ловим здесь;
//   3) вебхук-пути — только со своей приставкой (webhookId приставляет ensureWebhookIds,
//      литеральный parameters.path — prefixWebhookPaths; произвольное выражение в path —
//      отказ, оно может вычислиться в чужой путь);
// плюс запрет исполнения произвольного кода для shared-клиента. Белый список узлов —
// Ф3 (флаг каталога), подключится сюда же отдельной проверкой.

export interface ExecFenceViolation {
  stepId: string;
  title: string;
  kind: 'node' | 'cred' | 'ref' | 'webhook';
  detail: string;
}

/** Сборка на общий инстанс нарушает забор — деплой обязан отказать (не «починить молча»). */
export class ExecFenceError extends Error {
  readonly violations: ExecFenceViolation[];
  constructor(violations: ExecFenceViolation[]) {
    super(
      `забор общего сервера: ${violations.map((v) => `«${v.title}» — ${v.detail}`).join('; ')}`,
    );
    this.name = 'ExecFenceError';
    this.violations = violations;
  }
}

// ── Белый список узлов для ботов (SHARED-N8N Ф3) ─────────────────────────────────────
// ОДИН источник правды — config-ряд `bot_nodes` (JSON-массив полных nodeType, правится
// админкой): пикеры кабинета, ассистент (MCP search_nodes) и забор деплоя читают его,
// своих копий не держат. Нет ряда → пустой список → shared-клиенту недоступно ничего
// (fail-closed; сид кладёт миграция 20260728_bot_nodes_seed).
export const BOT_NODES_CONFIG_KEY = 'bot_nodes';

/** Приводит JSON config-ряда к списку nodeType: только строки, без дублей, с потолком. */
export function sanitizeBotNodes(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  for (const v of raw) {
    if (typeof v !== 'string' || !v || v.length > 200) continue;
    seen.add(v);
    if (seen.size >= 500) break;
  }
  return [...seen];
}

// Вне белого списка по праву: заметки на холсте не исполняются, узел-счётчик «Учёт
// шага» (GUARD_NODE_TYPE, step-guard.ts) — наш служебный: клиент его не выбирает,
// сборка деплоя вшивает сама (withStepGuards), поэтому в config `bot_nodes` (список
// узлов ДЛЯ клиента) его нет и не будет.
const WHITELIST_EXEMPT_NODE_TYPES: ReadonlySet<string> = new Set([
  'n8n-nodes-base.stickyNote',
  GUARD_NODE_TYPE,
]);

// Исполнение произвольного кода на общем сервере запрещено независимо от белого списка
// узлов (Ф3): это выход из среза клиента к чужим данным всего инстанса.
const SHARED_BANNED_NODE_TYPES: ReadonlySet<string> = new Set([
  'n8n-nodes-base.code',
  'n8n-nodes-base.function',
  'n8n-nodes-base.functionItem',
  'n8n-nodes-base.executeCommand',
  'n8n-nodes-base.ssh',
]);

// Узлы-ссылки на другой workflow: id → имя знает только инстанс, принадлежность по id
// проверить нечем — на общем отказ целиком (в белый список Ф3 они и не входят).
const WORKFLOW_REF_NODE_TYPES: ReadonlySet<string> = new Set([
  'n8n-nodes-base.executeWorkflow',
  '@n8n/n8n-nodes-langchain.toolWorkflow',
]);

/** Путь «возьми webhookId» у формы/чата — единственное разрешённое выражение в path. */
const WEBHOOK_PATH_EXPR = '={{ $webhookId }}';

/**
 * Нарушения забора общего инстанса в ГОТОВОЙ деплой-сборке. Выключенные узлы не
 * считаем: n8n их не исполняет, а включение = новое сохранение → забор отработает.
 * prefix '' (личный инстанс) — нарушений нет по определению.
 */
export function execFenceViolations(
  wf: WorkflowJson,
  opts: {
    prefix: string;
    credIds: ReadonlySet<string>;
    /** Белый список узлов (Ф3, config `bot_nodes`): передан → узел вне списка = отказ
     *  (страховка на случай, если UI/ассистент пропустили). Не передан — не проверяем
     *  (переходный вызов; штатные пути деплоя обязаны передавать). */
    allowedNodes?: ReadonlySet<string> | null;
  },
): ExecFenceViolation[] {
  const { prefix, credIds, allowedNodes } = opts;
  const out: ExecFenceViolation[] = [];
  if (!prefix) return out;
  for (const node of wf.nodes) {
    if (node.disabled === true) continue;
    const bad = (kind: ExecFenceViolation['kind'], detail: string): void => {
      out.push({ stepId: node.id, title: node.name, kind, detail });
    };
    const p = node.parameters ?? {};

    if (SHARED_BANNED_NODE_TYPES.has(node.type)) {
      bad('node', 'шаг с произвольным кодом недоступен на общем сервере');
    } else if (
      allowedNodes != null &&
      !allowedNodes.has(node.type) &&
      !WHITELIST_EXEMPT_NODE_TYPES.has(node.type)
    ) {
      bad('node', 'шаг недоступен на общем сервере (вне списка узлов для ботов)');
    }
    if (WORKFLOW_REF_NODE_TYPES.has(node.type)) {
      bad('ref', 'вызов другого сценария недоступен на общем сервере');
    }

    if (node.type === 'n8n-nodes-base.dataTable') {
      const rl = p.dataTableId as { mode?: string; value?: unknown } | undefined;
      if (rl?.mode === 'name') {
        if (!String(rl.value ?? '').startsWith(prefix)) bad('ref', 'таблица не принадлежит клиенту');
      } else if (rl && rl.value != null && rl.value !== '') {
        // Решение Ф4: реестра таблиц нет, принадлежность держит приставка в имени —
        // адресация по голому id непроверяема, на общем всегда отказ (имя канонично).
        bad('ref', 'адресация таблицы по id недоступна на общем сервере — только по имени');
      }
      if (typeof p.tableName === 'string' && p.tableName && !p.tableName.startsWith(prefix)) {
        bad('ref', 'имя создаваемой таблицы без приставки клиента');
      }
    }

    for (const [credType, cred] of Object.entries(node.credentials ?? {})) {
      if (!cred || !credIds.has(cred.id)) bad('cred', `аккаунт (${credType}) не принадлежит клиенту`);
    }

    if (isWebhookNodeType(node.type)) {
      if (typeof node.webhookId !== 'string' || !node.webhookId.startsWith(prefix)) {
        bad('webhook', 'вебхук-путь без приставки клиента');
      }
      const path = p.path;
      if (typeof path === 'string' && path && path !== WEBHOOK_PATH_EXPR) {
        if (path.startsWith('=')) bad('webhook', 'произвольное выражение в пути вебхука');
        else if (!path.startsWith(prefix)) bad('webhook', 'путь вебхука без приставки клиента');
      }
    }
  }
  return out;
}

/**
 * Таблицы данных на общем инстансе живут как `<userId>_<имя>` (SHARED-N8N Ф4):
 * литеральная адресация по имени (dataTableId mode:'name' и tableName шага «создать
 * таблицу») получает приставку клиента идемпотентно — ассистент/шаблоны пишут чистые
 * имена (bot_context и т.п.), приставку добавляет только сборка. Выражения не трогаем —
 * их отвергает забор (execFenceViolations), как и адресацию по голому id.
 */
function prefixDataTables(wf: WorkflowJson, prefix: string): WorkflowJson {
  const withPrefix = (v: string): string => (v.startsWith(prefix) ? v : `${prefix}${v}`);
  return {
    ...wf,
    nodes: wf.nodes.map((n) => {
      if (n.type !== DATA_TABLE_TYPE) return n;
      const p = n.parameters ?? {};
      let next: Record<string, unknown> | null = null;
      const rl = p.dataTableId as { mode?: string; value?: unknown } | undefined;
      if (rl?.mode === 'name' && typeof rl.value === 'string' && rl.value && !rl.value.startsWith('=')) {
        next = { ...p, dataTableId: { ...rl, value: withPrefix(rl.value) } };
      }
      if (typeof p.tableName === 'string' && p.tableName && !p.tableName.startsWith('=')) {
        next = { ...(next ?? p), tableName: withPrefix(p.tableName) };
      }
      return next ? { ...n, parameters: next } : n;
    }),
  };
}

/**
 * Литеральный parameters.path вебхук-узлов на общем инстансе получает приставку клиента
 * (идемпотентно): n8n регистрирует путь именно из path, webhookId — только запасной.
 * Выражения не трогаем — их отвергает забор (execFenceViolations).
 */
function prefixWebhookPaths(wf: WorkflowJson, prefix: string): WorkflowJson {
  return {
    ...wf,
    nodes: wf.nodes.map((n) => {
      if (!isWebhookNodeType(n.type)) return n;
      const path = (n.parameters ?? {}).path;
      if (typeof path !== 'string' || !path || path.startsWith('=') || path.startsWith(prefix)) return n;
      return { ...n, parameters: { ...(n.parameters ?? {}), path: `${prefix}${path}` } };
    }),
  };
}

// Настройки workflow при деплое из кабинета (web «Проверить» и автоперенос Ф4.4 —
// одна константа, копии дрейфуют). saveExecutionProgress — runData пишется ПО ХОДУ
// выполнения: питает живую подсветку шагов в кабинете во время пробного запуска.
export const DEPLOY_WORKFLOW_SETTINGS = {
  saveManualExecutions: true,
  saveDataErrorExecution: 'all',
  saveDataSuccessExecution: 'all',
  saveExecutionProgress: true,
} as const;

/**
 * Убрать из узлов ссылки на креды, которых нет среди известных id инстанса-получателя
 * (Ф4.4, автоперенос): id инстанса-источника после переезда мертвы, а чужой/мёртвый id
 * на общем валит забор целиком. Живую привязку заново делает bindWorkflowCredentials
 * внутри buildDeployWorkflow; узел, оставшийся без креда, кабинет честно покажет как
 * «нужен доступ» (workflowMissingCreds) — клиент подключает заново.
 */
export function stripUnknownWorkflowCredentials(
  wf: WorkflowJson,
  knownIds: ReadonlySet<string>,
): WorkflowJson {
  return {
    ...wf,
    nodes: wf.nodes.map((node) => {
      const entries = Object.entries(node.credentials ?? {});
      if (entries.length === 0) return node;
      const kept = entries.filter(([, ref]) => ref?.id != null && knownIds.has(ref.id));
      if (kept.length === entries.length) return node;
      const { credentials: _drop, ...rest } = node;
      return kept.length > 0 ? { ...rest, credentials: Object.fromEntries(kept) } : rest;
    }),
  };
}

// ── Подготовка к деплою (общее ядро edge scenario_deploy и web-кнопки «Запустить») ───
/** Шаги, у которых файл вложения качает САМА площадка (параметр `file` — ссылка). */
const SELF_FETCH_ATTACH_NODES: ReadonlySet<string> = new Set([
  'n8n-nodes-base.telegram',
  'CUSTOM.instagram',
]);

/**
 * Ссылки НАШИХ файлов у шагов-отправок — на адрес, до которого дотянется площадка.
 *
 * INVARIANT(scriptera): вложение телеграм- И инстаграм-шага обязано ссылаться на
 * ИНОСТРАННЫЙ вход. Причина: файл по ссылке качает сама площадка со своих серверов, а наш
 * прод стоит на российском адресе — Telegram отвечает «Bad Request: failed to get HTTP URL
 * content» (боевая проба 31.07), Мета же до российского адреса не доходит вовсе (тот же
 * фильтр, из-за которого наш собственный запрос к graph.instagram.com идёт тоннелем):
 * файл молча не приходит и отказа человек не видит (боевая проба 07.08 — «вкусняшка»
 * инстаграм-бота). Сам файл тот же (ищется по id) — меняются хост и ФОРМА пути: ссылка
 * переезжает на публичную `/f/<id>/<имя.ext>` (см. reachableFileUrl — без `/api/` и с
 * расширением, иначе Мета не обращается к серверу вовсе).
 * Чужие ссылки (клиент вписал свою) не трогаем — они и так живут снаружи.
 * Канон: docs/MESSAGES-PLAN.md М10.
 */
export function reachableChannelFiles(wf: WorkflowJson, base: string): WorkflowJson {
  const root = (base ?? '').replace(/\/+$/, '');
  if (!root || !Array.isArray(wf.nodes)) return wf;
  try {
    new URL(root);
  } catch {
    return wf;
  }
  let touched = false;
  const nodes = wf.nodes.map((node) => {
    if (!SELF_FETCH_ATTACH_NODES.has(canonicalNodeType(node.type))) return node;
    const file = (node.parameters ?? {}).file;
    // Выражение вместо ссылки ({{ … }}) и чужой адрес — не наш случай (reachableFileUrl
    // вернёт вход как есть, но лишний объект узла мы не плодим). Свои ссылки узнаём
    // ОДНИМ правилом на все формы (isOurFileUrl): публичная /f/<id>/<имя> и старые.
    if (typeof file !== 'string' || !isOurFileUrl(file)) return node;
    const next = reachableFileUrl(file, root);
    if (next === file) return node;
    touched = true;
    return { ...node, parameters: { ...node.parameters, file: next } };
  });
  return touched ? { ...wf, nodes } : wf;
}

export function buildDeployWorkflow(
  wf: WorkflowJson,
  opts: {
    /** Название в n8n = ТИТУЛ ЧЕЛОВЕКА (scenario_drafts.title); имя схемы — запасное. */
    title: string;
    /** Сид webhookId (draftId) — глобальная уникальность путей между сценариями. */
    seed: string;
    catalog: Map<string, CatalogNodeInfo>;
    creds: InstanceCredRef[];
    /** Общий инстанс (SHARED-N8N Ф2): приставка `<userId>_` (execPrefixOf) — имя
     *  workflow и все вебхук-пути уезжают с ней, сборка проходит забор
     *  (execFenceViolations, нарушение → throw ExecFenceError); личный — не передавать. */
    execPrefix?: string;
    /** Белый список узлов для ботов (Ф3): при execPrefix штатные пути деплоя обязаны
     *  передавать (readBotNodes / config `bot_nodes`) — узел вне списка = отказ забора. */
    execAllowedNodes?: ReadonlySet<string> | null;
    /** Иностранный вход (TG_DISPATCH_BASE): по нему Telegram и Мета качают вложения
     *  шагов — с российского адреса прода они их не берут (reachableChannelFiles). */
    fileBase?: string;
    /** Чек 54-ФЗ (PAYMENTS-PLAN П2): кассовые фрагменты по credId платёжного креда —
     *  вкладываются узлам оплаты параметром receiptDefaults (см. pay-receipt.ts).
     *  Не передан — узлы остаются как в каноне (чек не настраивался). */
    payReceipts?: ReadonlyMap<string, Record<string, unknown>>;
    /** Возврат покупателя (PAYMENTS-PLAN П5): базовые адреса нашей страницы «Оплата
     *  принята» по credId платёжного креда — вкладываются узлам ЮKassa/Stripe скрытым
     *  параметром `returnDefault` (см. pay-return.ts). Не передан — узлы как в каноне. */
    payReturns?: ReadonlyMap<string, string>;
    /** BOT-GUARD T2: схема со счётчиками не влезла в санитарный потолок узлов — счётчики
     *  НЕ вшиты, бот задеплоен без защиты. Сигнал — ВЛАДЕЛЬЦУ (recordError/notifyAdminTg
     *  на стороне вызывающего), НЕ клиенту: его канон свой лимит прошёл, потолок пробила
     *  наша вставка (запрет №7). */
    onGuardSkip?: (detail: string) => void;
    /** К6: учёт шага внутри наших узлов (флаг админки; см. step-guard.ts StepGuardOpts). */
    inlineGuard?: boolean;
  },
): WorkflowJson {
  const seed = opts.execPrefix ? `${opts.execPrefix}${opts.seed}` : opts.seed;
  // ACTION-STACK Э2: скрытая загрузка карточки человека. Шаг «Взять карточку человека» из
  // каталога убран (это кухня, а не работа клиента) — значит перед шагами и условиями,
  // которые читают карточку со своего входа, загрузку ставит сама сборка. В каноне
  // кабинета её нет: в редакторе и при обратном чтении человек её не видит. Доступ
  // Scriptera вкладываем сразу: до нового узла привязка по каталогу не дотягивается
  // (типа «Контакты» в схеме могло не быть вовсе, и каталог его не грузил).
  const scripteraCred = opts.creds.find((c) => c.type === 'scripteraAiApi') ?? null;
  // У9: кухня «Запросить номер телефона» — развилка «Номер прислали?» перед просьбой,
  // запись номера в карточку и снятие пометки. Врезаем ДО загрузок карточки: свою
  // загрузку эта кухня ставит сама (у развилки входов может быть несколько), а общая
  // вставка её видит и второй раз не повторяет. Канон механики — chat-phone.ts.
  const asked = withPhoneAsk(wf, scripteraCred);
  // Условия-пары («Подписан на канал», «Назначенные метки»): шаг-данные кабинет только
  // КЛАДЁТ рядом с карточкой, а в цепочку его ставит сборка (cond-pair.ts) — иначе пункты
  // каталога пришлось бы прятать у несоединённой карточки, а это забота не клиента.
  // ДО загрузок карточки: ряды про карточку человека сохраняет как раз вставка загрузки.
  const paired = withCondPairs(asked);
  const loaded = withContactLoaders(paired, scripteraCred);
  // BOT-GUARD T2: счётчик «Учёт шага» в каждое main-ребро каждого шага клиента — защита
  // от бесконечного круга + статистика узлов. СТРОГО после всей кухни (телефон, пары,
  // загрузки карточки): она врезается внутрь клиентских рёбер и сама счётчиков не
  // получает. Доступ Scriptera вкладываем сразу — bindWorkflowCredentials до нового
  // узла не дотянется (типа может не быть в каталоге сборки), как у загрузки карточки.
  const guarded = withStepGuards(loaded, {
    cred: scripteraCred,
    maxNodes: MAX_WORKFLOW_NODES,
    onSkip: opts.onGuardSkip,
    inlineGuard: opts.inlineGuard === true,
  });
  // Учёт проверок подписки Инстаграма (27.08): шагу «профиль собеседника» — ключ
  // Scriptera, чтобы узел докладывал исход проверки; необязательный доступ привязка по
  // каталогу не вкладывает (ig-profile-cred.ts).
  const igCounted = withIgProfileCred(guarded, scripteraCred);
  // Наш узел «Сообщение в Телеграм» (MESSAGE-COMPOSER Б6) ходит через шлюз — ключ
  // Scriptera вкладываем сами, по той же причине, что и соседям выше.
  const tgReady = withTgMessageCred(igCounted, scripteraCred);
  const reachable = reachableChannelFiles(tgReady, opts.fileBase ?? '');
  const hooked = ensureWebhookIds(bumpTelegramTriggers(reachable), seed, opts.execPrefix);
  const withHooks = opts.execPrefix
    ? prefixDataTables(prefixWebhookPaths(hooked, opts.execPrefix), opts.execPrefix)
    : hooked;
  // Чек и адрес возврата — ПОСЛЕ привязки доступов: ищутся по credId привязанного креда.
  const bound = injectPayReturnDefaults(
    injectPayReceipts(
      bindWorkflowCredentials(withHooks, opts.catalog, opts.creds),
      opts.payReceipts ?? new Map(),
    ),
    opts.payReturns ?? new Map(),
  );
  const clean = stripN8nAttribution(bound, opts.catalog);
  // INVARIANT(scriptera): имя на сервере — ТИТУЛ ЧЕЛОВЕКА (opts.title), а не wf.name.
  // Причина (владелец 2026-07-31): схема из шаблона всегда несёт своё имя («Чат-бот на
  // кодовое слово»), и при обратном приоритете название, которое человек дал сценарию
  // («Daria кодовое слово»), не доезжало на сервер НИКОГДА: до установки список показывал
  // титул, после установки — имя шаблона, и владелец решил, что мы переименовали его
  // сценарии. Имя схемы остаётся запасным — для сценариев, собранных в редакторе n8n,
  // откуда титул и берётся (packages/scenario-sync titleOf).
  const baseName = opts.title.trim() || (typeof wf.name === 'string' && wf.name.trim()) || 'Сценарий Scriptera';
  // Идемпотентно: уже приставленное имя (повторная сборка) второй раз не приставляем.
  const name =
    opts.execPrefix && !baseName.startsWith(opts.execPrefix) ? `${opts.execPrefix}${baseName}` : baseName;
  const built = { ...clean, name };
  if (opts.execPrefix) {
    const violations = execFenceViolations(built, {
      prefix: opts.execPrefix,
      credIds: new Set(opts.creds.map((c) => c.id)),
      allowedNodes: opts.execAllowedNodes,
    });
    if (violations.length > 0) throw new ExecFenceError(violations);
  }
  return built;
}
